import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import ObtieneDatosCookies from "../obtieneDatosCookiesUsuario";
import { generarPDFDeclaracionJurada } from "../Inscripciones/generarPDFDeclaracionJurada";
import UploadArchivosItem from "../Psicopedagogico/UploadAchivoItems";
import CarpetaMedicaItem from "../Sanidad/CarpetaMedicaItem";
import EvaluacionMedicaItem from "../Sanidad/EvaluacionMedicaItem";
import ModuloPiscologiaIngreso from "../Psicopedagogico/ModuloPiscologiaIngreso";
import ModuloCompletoPerfeccionamiento from "../Sanidad/ModuloCompletoPerfeccionamiento";
const API = "https://wwwa.isepsantafe.net.ar/expedientes/ingresos";

const configNota = (item) => {
  if (item?.tipo !== "evaluacion") return null;
  const observacion = String(item?.observacion ?? "").trim();
  if (/^numerico\s*(?:(?:>=|<=|>|<|=)\s*-?\d+(?:[.,]\d+)?)?$/i.test(observacion)) {
    return { tipo: "numerico", formato: "decimal", decimales: 2, minimo: 0, maximo: 10 };
  }
  if (item?.configuracionNumerica?.tipo === "numerico") return item.configuracionNumerica;
  try {
    const cfg = JSON.parse(observacion);
    return cfg?.tipo === "numerico" ? cfg : null;
  } catch {
    return null;
  }
};

const CampoNota = ({ item, persona, instancia, usuario, procesoId, onGuardado, onAvanzar }) => {
  const c = configNota(item);
  const [valor, setValor] = useState(item?.resultado ?? "");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setValor(item?.resultado ?? "");
  }, [item?.resultado, item?.id]);

  if (!c) return null;

  const decimales = c.formato === "entero" ? 0 : Number(c.decimales ?? 2);
  const minimo = c.minimo === "" || c.minimo == null ? 0 : Number(c.minimo);
  const maximo = c.maximo === "" || c.maximo == null ? 10 : Number(c.maximo);

  const guardar = async ({ avanzar = false } = {}) => {
    if (guardando) return;
    const texto = String(valor ?? "").trim().replace(",", ".");
    const patron = decimales === 0
      ? /^-?\d+$/
      : new RegExp(`^-?\\d+(?:\\.\\d{1,${decimales}})?$`);
    const numero = Number(texto);

    if (
      !texto ||
      !Number.isInteger(decimales) ||
      decimales < 0 ||
      decimales > 4 ||
      !patron.test(texto) ||
      !Number.isFinite(numero) ||
      numero < minimo ||
      numero > maximo
    ) {
      Swal.fire(
        "Nota inválida",
        `Ingresá un valor entre ${minimo} y ${maximo}, con hasta ${decimales} decimales.`,
        "warning"
      );
      return;
    }

    try {
      setGuardando(true);
      await axios.post(`${API}/guardarNotaNumericaItem`, {
        persona_id: persona?.dni,
        item_id: item.id,
        proceso_id: procesoId,
        nota: texto,
        usuario_id: usuario?.identificacion,
        usuario_nombre: `${usuario?.apellido || ""}, ${usuario?.nombre || ""}`,
      });
      // Al usar Enter, avanzar antes de actualizar el listado.
      // El refresco conserva el foco en el siguiente campo.
      if (avanzar && onAvanzar) onAvanzar();
      await onGuardado?.();
      if (!avanzar || !onAvanzar) {
        Swal.fire({ icon: "success", title: "Nota guardada", timer: 900, showConfirmButton: false });
      }
    } catch (e) {
      console.error("Error guardando nota:", e?.response?.data || e);
      Swal.fire("Error", e?.response?.data?.error || "No se pudo guardar la nota", "error");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 5 }}>
      <input
        aria-label={`Nota de ${item.nombre || item.descripcion}`}
        type="text"
        inputMode="decimal"
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            guardar({ avanzar: true });
          }
        }}
        placeholder={decimales ? `0,${"0".repeat(decimales)}` : "0"}
        style={{ width: 78, padding: 5, border: "1px solid #94a3b8", borderRadius: 5 }}
      />
      <button type="button" disabled={guardando} onClick={() => guardar()} style={{ padding: "5px 8px", borderRadius: 5 }}>
        {guardando ? "..." : "Guardar"}
      </button>
    </div>
  );
};

const ListadoInscriptoEtapa = ({
  etapa,
  procesoSeleccionado,
  presenteNuevo,
}) => {
  const Usuario = ObtieneDatosCookies();

  const [paginaActual, setPaginaActual] = useState(1);
  const [vistaCompacta, setVistaCompacta] = useState(true);
  const [modoCorreccion, setModoCorreccion] = useState(false);
  const [itemCorreccionId, setItemCorreccionId] = useState("");
  const [soloSinNota, setSoloSinNota] = useState(false);
  const [ordenAlfabetico, setOrdenAlfabetico] = useState("apellido_asc");
  const [filtroPresenteFecha, setFiltroPresenteFecha] = useState(null);
  const [filtroTipoAsistencia, setFiltroTipoAsistencia] = useState("todos");

  // Filtro por sede donde se tomó la asistencia.
  // todos | 1 Provincial | 2 Rosario | 3 Recreo
  const [filtroSede, setFiltroSede] = useState("todos");

  const [filtroTurno, setFiltroTurno] = useState(null);
  const [filtroEstadoItem, setFiltroEstadoItem] = useState("todos");
  const [filtroItemNombre, setFiltroItemNombre] = useState("todos");
  const [filtroFechaArchivoDesde, setFiltroFechaArchivoDesde] = useState("");

  const [itemSeleccionado, setItemSeleccionado] = useState(null);
  const [tipoModal, setTipoModal] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [data, setData] = useState([]);
  const [dataLocal, setDataLocal] = useState([]);
  const [presentesPorDni, setPresentesPorDni] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [modalDesmatricularOpen, setModalDesmatricularOpen] = useState(false);
  const [dnisDesmatricularTexto, setDnisDesmatricularTexto] = useState("");
  const [desmatriculando, setDesmatriculando] = useState(false);

  // Selección y calificación masiva de evaluaciones
  const [evaluacionesSeleccionadas, setEvaluacionesSeleccionadas] = useState({});
  const [filtroEvaluaciones, setFiltroEvaluaciones] = useState("pendientes");
  // Filtro general según el resultado de TODAS las instancias evaluables.
  // todos | aptos | no_aptos | pendientes
  const [filtroAptitudGeneral, setFiltroAptitudGeneral] = useState("todos");
  const [guardandoCalificacionMasiva, setGuardandoCalificacionMasiva] =
    useState(false);

  const filasPorPagina = 100;

  const clavePaginaStorage = useMemo(() => {
    const procesoId =
      etapa?.proceso_id ||
      procesoSeleccionado?.id ||
      "sin-proceso";

    const etapaId = etapa?.id || "sin-etapa";

    return `ListadoInscriptoEtapa:pagina:${procesoId}:${etapaId}`;
  }, [etapa?.proceso_id, etapa?.id, procesoSeleccionado?.id]);

  const cantidadMatriculadosEtapa = dataLocal.length;

  const esPerfeccionamiento =
    procesoSeleccionado?.Tipo?.trim()?.toLowerCase() === "perfeccionamiento";
  const abrirEvaluacionPerfeccionamiento = (persona, instanciaId, item) => {
    const itemSeguro = {
      ...(item || {}),
      id: item?.id || item?.item_id || item?.proceso_etapa_item_id || null,
      item_id: item?.id || item?.item_id || item?.proceso_etapa_item_id || null,
      proceso_etapa_item_id:
        item?.id || item?.item_id || item?.proceso_etapa_item_id || null,
      persona_proceso_item_id: item?.persona_proceso_item_id || null,
      tipo: item?.tipo || "evaluacion",
      observacion: item?.observacion || "Perfeccionamiento",
      nombre: item?.nombre || item?.descripcion || "Evaluación Perfeccionamiento",
      visible: item?.visible || "Sanidad",
      proceso_etapa_instancia_id:
        item?.proceso_etapa_instancia_id || instanciaId,
      debeCrearItem:
        !item?.id && !item?.item_id && !item?.proceso_etapa_item_id,
    };

    setItemSeleccionado({
      persona,
      instancia: instanciaId,
      item: itemSeguro,
      itemId: itemSeguro.id,
      item_id: itemSeguro.id,
      proceso_etapa_item_id: itemSeguro.proceso_etapa_item_id,
      persona_proceso_item_id: itemSeguro.persona_proceso_item_id,
      persona_id: persona?.id,
      persona_dni: persona?.dni,
      dni: persona?.dni,
      proceso_id: etapa?.proceso_id,
      proceso_etapa_id: etapa?.id,
      instancia_id: instanciaId,
      debeCrearItem: itemSeguro.debeCrearItem,
    });

    setTipoModal("completoPerfeccionamiento");
    setModalOpen(true);
  };
  const getTurno = (horaStr) => {
    if (!horaStr) return "Sin hora";

    const hora = parseInt(horaStr.split(":")[0], 10);

    if (hora < 8) return "Antes de 08:00";
    if (hora >= 8 && hora < 10) return "08:00 - 09:59";
    if (hora >= 10 && hora < 12) return "10:00 - 11:59";
    if (hora >= 12 && hora < 14) return "12:00 - 13:59";
    if (hora >= 14 && hora < 16) return "14:00 - 15:59";
    if (hora >= 16 && hora < 18) return "16:00 - 17:59";

    return "18:00 o más";
  };

  const getNombreItem = (item) => {
    return item?.descripcion || item?.nombre || "Sin nombre";
  };

  const normalizar = (valor) =>
    String(valor || "")
      .toLowerCase()
      .trim();

  const esEvaluacion = (item) =>
    normalizar(item?.tipo) === "evaluacion";

  const evaluacionTieneCorreccion = (item) => {
    if (!esEvaluacion(item)) return false;

    const estado = normalizar(item?.estado);
    const resultado = normalizar(item?.resultado);

    const estadosPendientes = [
      "",
      "pendiente",
      "sin corregir",
      "sin_corregir",
      "en_progreso",
    ];

    const tieneEstadoCorregido =
      estado && !estadosPendientes.includes(estado);

    const tieneResultado =
      resultado &&
      !["pendiente", "sin corregir", "sin_corregir"].includes(resultado);

    return Boolean(tieneEstadoCorregido || tieneResultado);
  };

  // =====================================================
  // APTO / NO APTO GENERAL SEGÚN INSTANCIAS
  // =====================================================
  // Regla:
  // - Si una instancia tiene al menos una evaluación NO APTO => NO_APTO.
  // - Si todas las evaluaciones de una instancia son APTO => APTO.
  // - Si falta resolver alguna evaluación => PENDIENTE.
  // - Resultado general de la persona:
  //     * cualquier instancia NO_APTO => NO_APTO
  //     * todas las instancias evaluables APTO => APTO
  //     * de lo contrario => PENDIENTE
  const getResultadoInstancia = (instancia) => {
    const evaluaciones =
      instancia?.items?.filter((item) => esEvaluacion(item)) || [];

    if (evaluaciones.length === 0) return "SIN_EVALUACION";

    const tieneNoApto = evaluaciones.some((item) => {
      const resultado = normalizar(item?.resultado).replace(/-/g, "_");
      const estado = normalizar(item?.estado);

      return (
        resultado === "no apto" ||
        resultado === "no_apto" ||
        estado === "rechazado"
      );
    });

    if (tieneNoApto) return "NO_APTO";

    const todasAptas = evaluaciones.every((item) => {
      const resultado = normalizar(item?.resultado);
      const estado = normalizar(item?.estado);

      return resultado === "apto" || estado === "aprobado";
    });

    return todasAptas ? "APTO" : "PENDIENTE";
  };

  const getResultadoGeneralPersona = (personaProceso) => {
    const detalleInstancias = (personaProceso?.instancias || [])
      .map((instancia) => ({
        id: instancia?.id,
        nombre: instancia?.nombre || `Instancia ${instancia?.id || ""}`,
        resultado: getResultadoInstancia(instancia),
      }))
      .filter(({ resultado }) => resultado !== "SIN_EVALUACION");

    if (detalleInstancias.length === 0) {
      return {
        resultado: "SIN_EVALUACION",
        totalInstancias: 0,
        aptas: 0,
        noAptas: 0,
        pendientes: 0,
        detalleInstancias: [],
      };
    }

    const aptas = detalleInstancias.filter(
      ({ resultado }) => resultado === "APTO"
    ).length;
    const noAptas = detalleInstancias.filter(
      ({ resultado }) => resultado === "NO_APTO"
    ).length;
    const pendientes = detalleInstancias.filter(
      ({ resultado }) => resultado === "PENDIENTE"
    ).length;

    let resultado = "PENDIENTE";

    if (noAptas > 0) resultado = "NO_APTO";
    else if (pendientes === 0 && aptas === detalleInstancias.length)
      resultado = "APTO";

    return {
      resultado,
      totalInstancias: detalleInstancias.length,
      aptas,
      noAptas,
      pendientes,
      detalleInstancias,
    };
  };

  const getItemIdReal = (item) =>
    item?.proceso_etapa_item_id ||
    item?.item_id ||
    item?.id ||
    null;

  const getClaveEvaluacion = (personaProceso, instancia, item) =>
    [
      personaProceso?.persona?.dni ||
        personaProceso?.persona_id ||
        personaProceso?.persona?.id,
      instancia?.id,
      getItemIdReal(item),
    ].join("-");

  const getEstadoNormalizadoItem = (item) => {
    if (item?.tipo === "formulario") {
      const estadoForm = normalizar(item.formulario_estado);
      if (estadoForm === "enviado") return "completado";
      if (estadoForm === "borrador") return "en_progreso";
      return "pendiente";
    }

    const estado = normalizar(item?.estado);
    if (estado === "aprobado" || estado === "completado") return "completado";
    if (estado === "rechazado" || estado === "no_apto") return "rechazado";
    if (estado === "en_progreso" || estado === "cargado") return "en_progreso";
    return "pendiente";
  };

  const getColor = (item) => {
    const estado = getEstadoNormalizadoItem(item);
    if (estado === "completado") return "#bbf7d0"; // verde: aprobado
    if (estado === "rechazado") return "#fecdd3"; // rosa: rechazado
    if (estado === "en_progreso") return "#fef9c3"; // amarillo: nota cargada sin regla
    return "#f1f5f9"; // gris: sin nota o pendiente
  };

  const getIcon = (item) => {
    const estado = getEstadoNormalizadoItem(item);
    if (estado === "completado") return "✔";
    if (estado === "rechazado") return "✖";
    if (estado === "en_progreso") return "●";
    return "○";
  };

  const getTextoTipoArchivo = (item) => {
    if (item?.tipo === "archivo") return "📎 ";
    return "";
  };

  const getValorFechaArchivo = (archivo) => {
    return (
      archivo?.createdAt ||
      archivo?.created_at ||
      archivo?.fecha_creacion ||
      archivo?.fecha ||
      null
    );
  };

  const itemCumpleFechaArchivo = (item, fechaDesde) => {
    if (!fechaDesde || item?.tipo !== "archivo") return false;

    const fechaFiltro = new Date(`${fechaDesde}T00:00:00`);

    if (Number.isNaN(fechaFiltro.getTime())) return false;

    return (
      Array.isArray(item.archivos) &&
      item.archivos.some((archivo) => {
        const valorFecha = getValorFechaArchivo(archivo);

        if (!valorFecha) return false;

        const fechaArchivo = new Date(valorFecha);

        if (Number.isNaN(fechaArchivo.getTime())) return false;

        return fechaArchivo >= fechaFiltro;
      })
    );
  };

  const tieneArchivoDesdeFecha = (fila, fechaDesde) => {
    if (!fechaDesde) return true;

    const fechaFiltro = new Date(`${fechaDesde}T00:00:00`);

    if (Number.isNaN(fechaFiltro.getTime())) return true;

    return fila.instancias?.some((instancia) =>
      instancia.items?.some((item) =>
        Array.isArray(item.archivos) &&
        item.archivos.some((archivo) => {
          const valorFecha = getValorFechaArchivo(archivo);

          if (!valorFecha) return false;

          const fechaArchivo = new Date(valorFecha);

          if (Number.isNaN(fechaArchivo.getTime())) return false;

          return fechaArchivo >= fechaFiltro;
        })
      )
    );
  };

  const getFechaArchivo = (item) => {
    if (item?.tipo !== "archivo" || !Array.isArray(item.archivos) || item.archivos.length === 0) {
      return null;
    }

    const archivoMasReciente = [...item.archivos].sort((a, b) => {
      const fechaA = new Date(getValorFechaArchivo(a) || 0).getTime();
      const fechaB = new Date(getValorFechaArchivo(b) || 0).getTime();

      return fechaB - fechaA;
    })[0];

    const valorFecha = getValorFechaArchivo(archivoMasReciente);

    if (!valorFecha) return null;

    const fecha = new Date(valorFecha);

    if (Number.isNaN(fecha.getTime())) return null;

    return fecha.toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };



  const normalizarDnis = (valor) => {
    return Array.from(
      new Set(
        String(valor || "")
          .split(/[\s,;]+/)
          .map((dni) => dni.replace(/\D/g, "").trim())
          .filter(Boolean)
      )
    );
  };

  const obtenerPersonaPorDni = (dni) => {
    return dataLocal.find(
      (fila) => String(fila.persona?.dni) === String(dni)
    );
  };

  const confirmarYDesmatricular = async (
    dnisEntrada,
    { cerrarModal = false } = {}
  ) => {
    const dnis = normalizarDnis(
      Array.isArray(dnisEntrada) ? dnisEntrada.join(" ") : dnisEntrada
    );

    if (!dnis.length) {
      Swal.fire("Atención", "Ingresá al menos un DNI válido.", "warning");
      return;
    }

    const encontrados = dnis
      .map((dni) => obtenerPersonaPorDni(dni))
      .filter(Boolean);

    const dnisEncontrados = encontrados.map((fila) =>
      String(fila.persona?.dni)
    );

    const dnisNoEncontrados = dnis.filter(
      (dni) => !dnisEncontrados.includes(String(dni))
    );

    const personasHtml = encontrados.length
      ? encontrados
          .map(
            (fila) => `
              <div style="padding:8px 10px;margin-bottom:6px;border:1px solid #fecaca;border-radius:7px;background:#fff7f7;text-align:left;">
                <b>${fila.persona?.apellido || ""}, ${
                  fila.persona?.nombre || ""
                }</b><br/>
                DNI: ${fila.persona?.dni || ""}
              </div>
            `
          )
          .join("")
      : `
          <div style="padding:10px;border:1px solid #fde68a;border-radius:7px;background:#fffbeb;text-align:left;">
            Los DNI ingresados no aparecen actualmente en el listado,
            pero igualmente se enviarán al servidor.
          </div>
        `;

    const noEncontradosHtml = dnisNoEncontrados.length
      ? `
          <div style="margin-top:12px;padding:10px;border:1px solid #fde68a;border-radius:7px;background:#fffbeb;text-align:left;">
            <b>DNI no encontrados en el listado actual:</b><br/>
            ${dnisNoEncontrados.join(", ")}
          </div>
        `
      : "";

    const confirmacion = await Swal.fire({
      title:
        dnis.length === 1
          ? "¿Desmatricular esta persona?"
          : `¿Desmatricular ${dnis.length} personas?`,
      html: `
        <div style="max-height:420px;overflow:auto;padding-right:4px;">
          <div style="margin-bottom:12px;padding:10px;border-radius:7px;background:#fee2e2;color:#991b1b;text-align:left;">
            Esta acción quitará a las personas de la etapa
            <b>${etapa?.nombre || ""}</b>.
          </div>
          ${personasHtml}
          ${noEncontradosHtml}
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText:
        dnis.length === 1
          ? "Sí, desmatricular"
          : `Sí, desmatricular ${dnis.length}`,
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      width: 650,
      reverseButtons: true,
    });

    if (!confirmacion.isConfirmed) return;

    try {
      setDesmatriculando(true);

      Swal.fire({
        title: "Desmatriculando...",
        text: "Procesando la solicitud",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });
// console.log(etapa.proceso_id)
// console.log(procesoSeleccionado?.id)
// console.log(etapa.id)


     const respuesta = await axios.post(
  `${API}/desmatricularMasivoEtapa`,
  {
    dnis,
    proceso_id: etapa.proceso_id || procesoSeleccionado?.id,
    proceso_etapa_id: etapa.id,
     usuario_id: Usuario.identificacion,
  usuario_nombre: `${Usuario.apellido}, ${Usuario.nombre}`,
  }
);
// console.log(respuesta)

      await fetchData({ silencioso: true });
      await fetchPresentes({ silencioso: true });

      if (cerrarModal) {
        setModalDesmatricularOpen(false);
        setDnisDesmatricularTexto("");
      }

      Swal.fire({
        title: "Desmatriculación completada",
        html: `
          <div style="text-align:left">
            <b>Personas solicitadas:</b> ${dnis.length}<br/>
            <b>Registros eliminados:</b> ${
              respuesta.data?.eliminados ?? "Sin detalle"
            }
          </div>
        `,
        icon: "success",
      });
    } catch (error) {
      console.error(error);

      Swal.fire({
        title: "Error",
        text:
          error.response?.data?.error ||
          "No se pudo completar la desmatriculación.",
        icon: "error",
      });
    } finally {
      setDesmatriculando(false);
    }
  };

  const desmatricularPersona = (persona) => {
    confirmarYDesmatricular([persona?.dni]);
  };

  const desmatricularMasivamente = () => {
    confirmarYDesmatricular(dnisDesmatricularTexto, {
      cerrarModal: true,
    });
  };

  const descargarZipItem = async (item) => {
    try {
      Swal.fire({
        title: "Generando ZIP...",
        text: "Preparando archivos, SCL90 y declaraciones juradas",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const dnis = filteredData.map((p) => p.persona?.dni).filter(Boolean);

      const res = await axios.post(
        `${API}/descargarZipArchivosItemPorEtapa`,
        {
          proceso_id: etapa.proceso_id,
          proceso_etapa_id: etapa.id,
          item_id: item.id,
          formulario_id: item.formulario_id || null,
          dnis,
          incluir_archivos: true,
          incluir_formularios: true,
          incluir_scl90: true,
          etapa_psico: etapa.nombre || "Auxiliar Forense 2026",
        },
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([res.data], { type: "application/zip" });

      saveAs(blob, `documentos_${getNombreItem(item)}.zip`);

      Swal.close();
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo descargar el ZIP", "error");
    }
  };

  const esEntregaCarpetaMedica = (item) => {
    return (
      item?.tipo === "archivo" &&
      item?.descripcion?.toLowerCase().includes("entrega carpeta medica")
    );
  };

  const abrirModal = (persona, instanciaId, item, tipo) => {
    setItemSeleccionado({
      persona,
      instancia: instanciaId,
      item,
      persona_id: persona?.id,
      persona_dni: persona?.dni,
      dni: persona?.dni,
      item_id: item?.id,
      proceso_etapa_item_id: item?.id,
      proceso_id: etapa?.proceso_id,
      proceso_etapa_id: etapa?.id,
      instancia_id: instanciaId,
      idPersona: persona?.id,
      idItem: item?.id,
    });

    setTipoModal(tipo);
    setModalOpen(true);
  };

  const abrirArchivoItem = (persona, instanciaId, item) => {
    if (esEntregaCarpetaMedica(item)) {
      abrirModal(persona, instanciaId, item, "carpeta_medica");
      return;
    }

    abrirModal(persona, instanciaId, item, "archivo");
  };


  // =====================================================
  // PRESENTES DE TODAS LAS DEPENDENCIAS
  // =====================================================
  const getPresentesPersona = (fila) => {
    const dni = String(fila?.persona?.dni || "").trim();

    const presentesNuevos = presentesPorDni[dni];

    if (Array.isArray(presentesNuevos) && presentesNuevos.length > 0) {
      return presentesNuevos;
    }

    // Compatibilidad temporal con getPersonasPorEtapa,
    // que todavía puede devolver un único "presente".
    if (fila?.presente) {
      return [fila.presente];
    }

    return [];
  };

  const getNombreSede = (sedeID) => {
    const mapaSedes = {
      1: "Provincial",
      2: "Rosario",
      3: "Recreo",
    };

    if (
      sedeID === null ||
      sedeID === undefined ||
      sedeID === ""
    ) {
      return "Sin sede";
    }

    return (
      mapaSedes[Number(sedeID)] ||
      `Sede ${sedeID}`
    );
  };

  const getSedesPersona = (personaProceso) => {
    const presentes =
      getPresentesPersona(personaProceso);

    const sedes = presentes
      .map((presente) =>
        getNombreSede(presente?.sedeID)
      )
      .filter(Boolean);

    return [...new Set(sedes)];
  };

  const getColorDependencia = (dependencia) => {
    const texto = normalizar(
      dependencia || "sin dependencia"
    );

    const colores = [
      "#14532d",
      "#166534",
      "#15803d",
      "#16a34a",
      "#22c55e",
      "#4ade80",
      "#059669",
      "#047857",
      "#0f766e",
    ];

    let hash = 0;

    for (let i = 0; i < texto.length; i++) {
      hash = (hash * 31 + texto.charCodeAt(i)) >>> 0;
    }

    return colores[hash % colores.length];
  };

  const getColorTextoDependencia = (dependencia) => {
    const color = getColorDependencia(dependencia);

    return color === "#4ade80" || color === "#22c55e"
      ? "#052e16"
      : "#ffffff";
  };

  const fetchPresentes = async ({ silencioso = false } = {}) => {
    try {
      if (!etapa?.id || esPerfeccionamiento) {
        setPresentesPorDni({});
        return;
      }

      const res = await axios.post(
        `${API}/listarPresentesEtapa`,
        {
          proceso_etapa_id: etapa.id,
        }
      );

      const presentes = Array.isArray(res.data?.presentes)
        ? res.data.presentes
        : [];

      const mapa = {};

      presentes.forEach((presente) => {
        const dni = String(presente?.dni || "").trim();

        if (!dni) return;

        if (!mapa[dni]) {
          mapa[dni] = [];
        }

        mapa[dni].push(presente);
      });

      // Ordenamos por fecha/hora, conservando todos los registros.
      Object.keys(mapa).forEach((dni) => {
        mapa[dni] = mapa[dni].sort((a, b) => {
          const valorA = `${a?.fecha || ""} ${a?.hora || ""}`;
          const valorB = `${b?.fecha || ""} ${b?.hora || ""}`;

          return valorA.localeCompare(valorB);
        });
      });

      setPresentesPorDni(mapa);
    } catch (error) {
      console.error(
        "Error cargando presentes de todas las dependencias:",
        error
      );

      if (!silencioso) {
        Swal.fire({
          icon: "warning",
          title: "Presentes",
          text:
            error?.response?.data?.error ||
            "No se pudieron cargar los presentes de las dependencias.",
        });
      }
    }
  };

  // =====================================================
  // AGREGAR PRESENTE NUEVO EN PANTALLA
  // SIN HACER OTRA CONSULTA
  // =====================================================
  const agregarPresenteLocal = (registro) => {
    if (!registro) return;

    const dni = String(
      registro?.dni || ""
    ).trim();

    if (!dni) return;

    const etapaRegistro = String(
      registro?.proceso_etapa_id || ""
    );

    const etapaActual = String(
      etapa?.id || ""
    );

    // No pintamos un registro que pertenece a otra etapa.
    if (
      etapaRegistro &&
      etapaActual &&
      etapaRegistro !== etapaActual
    ) {
      return;
    }

    setPresentesPorDni((prev) => {
      const actuales = Array.isArray(
        prev[dni]
      )
        ? prev[dni]
        : [];

      const dependenciaNueva = String(
        registro?.dependencia || ""
      ).trim();

      const fechaNueva = String(
        registro?.fecha || ""
      ).trim();

      const horaNueva = String(
        registro?.hora || ""
      ).trim();

      // Evitamos duplicar el mismo presente si por alguna
      // razón el callback llega más de una vez.
      const yaExiste = actuales.some(
        (presente) => {
          if (
            registro?.id &&
            presente?.id
          ) {
            return (
              String(presente.id) ===
              String(registro.id)
            );
          }

          return (
            String(
              presente?.dependencia || ""
            ).trim() ===
              dependenciaNueva &&
            String(
              presente?.fecha || ""
            ).trim() ===
              fechaNueva &&
            String(
              presente?.hora || ""
            ).trim() ===
              horaNueva
          );
        }
      );

      if (yaExiste) {
        return prev;
      }

      const nuevoRegistro = {
        ...registro,
        dni,
        proceso_etapa_id:
          registro?.proceso_etapa_id ||
          etapa?.id,
      };

      const nuevosPresentes = [
        ...actuales,
        nuevoRegistro,
      ].sort((a, b) => {
        const valorA = `${a?.fecha || ""} ${
          a?.hora || ""
        }`;

        const valorB = `${b?.fecha || ""} ${
          b?.hora || ""
        }`;

        return valorA.localeCompare(
          valorB
        );
      });

      return {
        ...prev,
        [dni]: nuevosPresentes,
      };
    });
  };

  const fetchData = async ({ silencioso = false } = {}) => {
    try {
      if (!silencioso) {
        setLoading(true);
      }

      const res = await axios.post(`${API}/getPersonasPorEtapa`, {
        proceso_etapa_id: etapa.id,
      });

      setData(res.data?.personas || []);
    } catch (err) {
      console.error(err);

      if (!silencioso) {
        Swal.fire("Error", "No se pudo cargar la etapa", "error");
      }
    } finally {
      if (!silencioso) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!etapa?.id) return;

    const paginaGuardada = Number(
      localStorage.getItem(clavePaginaStorage)
    );

    setPaginaActual(
      Number.isInteger(paginaGuardada) && paginaGuardada > 0
        ? paginaGuardada
        : 1
    );

    fetchData();
    fetchPresentes();
  }, [etapa, clavePaginaStorage]);

  useEffect(() => {
    setDataLocal(data);
  }, [data]);

  useEffect(() => {
    if (!etapa?.id) return;

    localStorage.setItem(
      clavePaginaStorage,
      String(paginaActual)
    );
  }, [paginaActual, clavePaginaStorage, etapa?.id]);

  // =====================================================
  // PRESENTE NUEVO DESDE EL LECTOR
  // =====================================================
  // ProcesosViewer recibe el registro del backend y lo
  // pasa por prop. Acá lo incorporamos al mapa local.
  // NO ejecutamos fetchPresentes().
  useEffect(() => {
    if (!presenteNuevo) return;

    agregarPresenteLocal(
      presenteNuevo
    );
  }, [
    presenteNuevo,
    etapa?.id,
  ]);

  useEffect(() => {
    setPaginaActual(1);
  }, [
    search,
    filtroPresenteFecha,
    filtroTipoAsistencia,
    filtroSede,
    filtroTurno,
    filtroEstadoItem,
    filtroItemNombre,
    filtroFechaArchivoDesde,
    filtroEvaluaciones,
    filtroAptitudGeneral,
  ]);

  const itemsDisponibles = useMemo(() => {
    const setItems = new Set();

    dataLocal.forEach((p) => {
      p.instancias?.forEach((inst) => {
        inst.items?.forEach((item) => {
          setItems.add(getNombreItem(item));
        });
      });
    });

    return Array.from(setItems).sort();
  }, [dataLocal]);

  const filteredData = useMemo(() => {
    return dataLocal.filter((fila) => {
      const p = fila.persona || {};
      const texto = `${p.dni} ${p.nombre} ${p.apellido}`.toLowerCase();

      const cumpleBusqueda = texto.includes(search.toLowerCase());

      const presentesFila = getPresentesPersona(fila);

      // El presente debe coincidir en el MISMO registro con
      // la fecha y la sede seleccionadas.
      const tienePresenteFechaYSede =
        !esPerfeccionamiento &&
        filtroPresenteFecha
          ? presentesFila.some((presente) => {
              const coincideFecha =
                String(presente?.fecha || "") ===
                String(filtroPresenteFecha);

              const coincideSede =
                filtroSede === "todos" ||
                String(presente?.sedeID || "") ===
                  String(filtroSede);

              return coincideFecha && coincideSede;
            })
          : false;

      const cumplePresenteFecha =
        !esPerfeccionamiento && filtroPresenteFecha
          ? filtroTipoAsistencia === "restantes"
            ? !tienePresenteFechaYSede
            : filtroTipoAsistencia === "presentes"
            ? tienePresenteFechaYSede
            : true
          : true;

      // Si todavía no elegimos fecha pero sí elegimos sede,
      // mostramos personas que tengan al menos un presente en esa sede.
      const cumpleSede =
        !esPerfeccionamiento &&
        filtroSede !== "todos" &&
        !filtroPresenteFecha
          ? presentesFila.some(
              (presente) =>
                String(presente?.sedeID || "") ===
                String(filtroSede)
            )
          : true;

      const cumpleTurno =
        !esPerfeccionamiento && filtroTurno
          ? presentesFila.some((presente) => {
              const coincideTurno =
                getTurno(presente?.hora) === filtroTurno;

              const coincideSede =
                filtroSede === "todos" ||
                String(presente?.sedeID || "") ===
                  String(filtroSede);

              return coincideTurno && coincideSede;
            })
          : true;

      const cumpleFechaArchivo = tieneArchivoDesdeFecha(
        fila,
        filtroFechaArchivoDesde
      );

      const cumpleItemYEstado =
        filtroItemNombre === "todos"
          ? filtroEstadoItem === "todos"
            ? true
            : fila.instancias?.some((inst) =>
              inst.items?.some(
                (it) => getEstadoNormalizadoItem(it) === filtroEstadoItem
              )
            )
          : fila.instancias?.some((inst) =>
            inst.items?.some((it) => {
              const cumpleNombre = getNombreItem(it) === filtroItemNombre;

              const cumpleEstado =
                filtroEstadoItem === "todos"
                  ? true
                  : getEstadoNormalizadoItem(it) === filtroEstadoItem;

              return cumpleNombre && cumpleEstado;
            })
          );

      const evaluacionesFila =
        fila.instancias
          ?.flatMap((inst) => inst.items || [])
          .filter((item) => esEvaluacion(item)) || [];

      const cumpleFiltroEvaluaciones =
        filtroEvaluaciones === "todos"
          ? true
          : filtroEvaluaciones === "evaluados"
          ? evaluacionesFila.some((item) =>
              evaluacionTieneCorreccion(item)
            )
          : evaluacionesFila.some(
              (item) => !evaluacionTieneCorreccion(item)
            );

      const resultadoGeneral = getResultadoGeneralPersona(fila).resultado;

      const cumpleAptitudGeneral =
        filtroAptitudGeneral === "todos"
          ? true
          : filtroAptitudGeneral === "aptos"
          ? resultadoGeneral === "APTO"
          : filtroAptitudGeneral === "no_aptos"
          ? resultadoGeneral === "NO_APTO"
          : resultadoGeneral === "PENDIENTE" ||
            resultadoGeneral === "SIN_EVALUACION";

      return (
        cumpleBusqueda &&
        cumplePresenteFecha &&
        cumpleSede &&
        cumpleTurno &&
        cumpleFechaArchivo &&
        cumpleItemYEstado &&
        cumpleFiltroEvaluaciones &&
        cumpleAptitudGeneral
      );
    });
  }, [
    dataLocal,
    search,
    filtroPresenteFecha,
    filtroTipoAsistencia,
    filtroSede,
    filtroTurno,
    filtroItemNombre,
    filtroEstadoItem,
    filtroFechaArchivoDesde,
    filtroEvaluaciones,
    filtroAptitudGeneral,
    esPerfeccionamiento,
    presentesPorDni,
  ]);

  // Evaluaciones numéricas disponibles según los filtros actuales.
  const itemsNumericosDisponibles = useMemo(() => {
    const mapa = new Map();
    dataLocal.forEach((fila) => fila.instancias?.forEach((inst) =>
      inst.items?.forEach((item) => {
        if (!configNota(item)) return;
        const id = String(getItemIdReal(item));
        if (!mapa.has(id)) mapa.set(id, { id, nombre: getNombreItem(item), instancia: inst.nombre });
      })
    ));
    return [...mapa.values()];
  }, [dataLocal]);

  const dataOrdenada = useMemo(() => {
    const lista = [...filteredData];

    const compararTexto = (a, b) =>
      String(a || "").localeCompare(
        String(b || ""),
        "es",
        {
          sensitivity: "base",
          numeric: true,
        }
      );

    lista.sort((filaA, filaB) => {
      const personaA = filaA?.persona || {};
      const personaB = filaB?.persona || {};

      if (ordenAlfabetico === "apellido_desc") {
        const porApellido = compararTexto(
          personaB.apellido,
          personaA.apellido
        );

        if (porApellido !== 0) return porApellido;

        return compararTexto(
          personaB.nombre,
          personaA.nombre
        );
      }

      if (ordenAlfabetico === "nombre_asc") {
        const porNombre = compararTexto(
          personaA.nombre,
          personaB.nombre
        );

        if (porNombre !== 0) return porNombre;

        return compararTexto(
          personaA.apellido,
          personaB.apellido
        );
      }

      if (ordenAlfabetico === "nombre_desc") {
        const porNombre = compararTexto(
          personaB.nombre,
          personaA.nombre
        );

        if (porNombre !== 0) return porNombre;

        return compararTexto(
          personaB.apellido,
          personaA.apellido
        );
      }

      // Por defecto: apellido A → Z.
      const porApellido = compararTexto(
        personaA.apellido,
        personaB.apellido
      );

      if (porApellido !== 0) return porApellido;

      return compararTexto(
        personaA.nombre,
        personaB.nombre
      );
    });

    return lista;
  }, [filteredData, ordenAlfabetico]);

  const filasCorreccion = useMemo(() => {
    if (!itemCorreccionId) return [];
    return dataOrdenada.filter((fila) => {
      const encontrado = fila.instancias?.some((inst) => inst.items?.some((item) =>
        String(getItemIdReal(item)) === String(itemCorreccionId) &&
        configNota(item) &&
        (!soloSinNota || item.resultado === null || item.resultado === undefined || String(item.resultado).trim() === "")
      ));
      return encontrado;
    });
  }, [dataOrdenada, itemCorreccionId, soloSinNota]);

  const totalPaginas =
    Math.ceil(dataOrdenada.length / filasPorPagina) || 1;

  useEffect(() => {
    if (paginaActual > totalPaginas) {
      setPaginaActual(totalPaginas);
    }
  }, [paginaActual, totalPaginas]);

  const dataPaginada = useMemo(() => {
    const inicio = (paginaActual - 1) * filasPorPagina;
    const fin = inicio + filasPorPagina;

    return dataOrdenada.slice(inicio, fin);
  }, [dataOrdenada, paginaActual]);


  const evaluacionesVisibles = useMemo(() => {
    const lista = [];

    filteredData.forEach((personaProceso) => {
      personaProceso.instancias?.forEach((instancia) => {
        instancia.items
          ?.filter((item) =>
            String(item.visible || "")
              .toLowerCase()
              .includes("reclutamiento")
          )
          .forEach((item) => {
            if (!esEvaluacion(item)) return;

            if (
              filtroEvaluaciones === "evaluados" &&
              !evaluacionTieneCorreccion(item)
            ) {
              return;
            }

            if (
              filtroEvaluaciones === "pendientes" &&
              evaluacionTieneCorreccion(item)
            ) {
              return;
            }

            const clave = getClaveEvaluacion(
              personaProceso,
              instancia,
              item
            );

            lista.push({
              clave,
              personaProceso,
              instancia,
              item,
            });
          });
      });
    });

    return lista;
  }, [filteredData, filtroEvaluaciones]);

  const cantidadSeleccionadas = Object.keys(
    evaluacionesSeleccionadas
  ).length;

  const todasLasEvaluacionesVisiblesSeleccionadas =
    evaluacionesVisibles.length > 0 &&
    evaluacionesVisibles.every(
      ({ clave }) => evaluacionesSeleccionadas[clave]
    );

  const toggleEvaluacionSeleccionada = (
    personaProceso,
    instancia,
    item
  ) => {
    const clave = getClaveEvaluacion(
      personaProceso,
      instancia,
      item
    );

    setEvaluacionesSeleccionadas((prev) => {
      const siguiente = { ...prev };

      if (siguiente[clave]) {
        delete siguiente[clave];
      } else {
        siguiente[clave] = {
          clave,
          personaProceso,
          instancia,
          item,
        };
      }

      return siguiente;
    });
  };

  const toggleSeleccionarTodasEvaluaciones = () => {
    setEvaluacionesSeleccionadas((prev) => {
      const siguiente = { ...prev };

      if (todasLasEvaluacionesVisiblesSeleccionadas) {
        evaluacionesVisibles.forEach(({ clave }) => {
          delete siguiente[clave];
        });
      } else {
        evaluacionesVisibles.forEach((registro) => {
          siguiente[registro.clave] = registro;
        });
      }

      return siguiente;
    });
  };

  const limpiarSeleccionEvaluaciones = () => {
    setEvaluacionesSeleccionadas({});
  };

  const aplicarGuardadosMasivosEnPantalla = (guardados = []) => {
    if (!Array.isArray(guardados) || guardados.length === 0) return;

    const mapaGuardados = new Map(
      guardados.map((guardado) => [
        `${String(guardado.persona_id)}-${String(guardado.item_id)}`,
        guardado,
      ])
    );

    setDataLocal((prev) =>
      prev.map((personaProceso) => {
        const personaId =
          personaProceso?.persona?.dni ||
          personaProceso?.persona_id ||
          personaProceso?.persona?.id;

        return {
          ...personaProceso,
          instancias:
            personaProceso.instancias?.map((instancia) => ({
              ...instancia,
              items:
                instancia.items?.map((item) => {
                  const itemId = getItemIdReal(item);

                  const guardado = mapaGuardados.get(
                    `${String(personaId)}-${String(itemId)}`
                  );

                  if (!guardado) return item;

                  return {
                    ...item,
                    estado: guardado.estado,
                    resultado: guardado.resultado,
                    persona_proceso_item_id:
                      guardado.persona_proceso_item_id ||
                      item.persona_proceso_item_id,
                  };
                }) || [],
            })) || [],
        };
      })
    );
  };

  const calificarSeleccionadasMasivamente = async () => {
    const seleccionadas = Object.values(
      evaluacionesSeleccionadas
    );

    if (!seleccionadas.length) {
      Swal.fire(
        "Sin evaluaciones",
        "Seleccioná al menos una evaluación.",
        "info"
      );
      return;
    }

    const { value: formulario } = await Swal.fire({
      title: `Calificar ${seleccionadas.length} evaluación(es)`,
      html: `
        <div style="text-align:left">
          <label style="display:block;font-weight:bold;margin-bottom:6px">
            Resultado
          </label>

          <select
            id="swal-resultado-masivo"
            class="swal2-select"
            style="width:100%;margin:0 0 14px 0"
          >
            <option value="">Seleccione...</option>
            <option value="apto">APTO</option>
            <option value="no_apto">NO APTO</option>
          </select>

          <div
            id="contenedor-motivos-no-apto"
            style="display:none;margin-bottom:14px"
          >
            <label style="display:block;font-weight:bold;margin-bottom:6px">
              Motivos de NO APTO
            </label>

            <textarea
              id="swal-motivos-no-apto"
              class="swal2-textarea"
              placeholder="Ingresá los motivos de NO APTO"
              style="width:100%;margin:0;min-height:90px"
            ></textarea>
          </div>

          <label style="display:block;font-weight:bold;margin-bottom:6px">
            Observación
          </label>

          <textarea
            id="swal-observacion-masiva"
            class="swal2-textarea"
            placeholder="Observación adicional"
            style="width:100%;margin:0;min-height:100px"
          ></textarea>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Continuar",
      cancelButtonText: "Cancelar",
      focusConfirm: false,
      didOpen: () => {
        const selectResultado = document.getElementById(
          "swal-resultado-masivo"
        );

        const contenedorMotivos = document.getElementById(
          "contenedor-motivos-no-apto"
        );

        const actualizarMotivos = () => {
          const esNoApto =
            selectResultado?.value === "no_apto";

          if (contenedorMotivos) {
            contenedorMotivos.style.display = esNoApto
              ? "block"
              : "none";
          }

          if (!esNoApto) {
            const inputMotivos = document.getElementById(
              "swal-motivos-no-apto"
            );

            if (inputMotivos) inputMotivos.value = "";
          }
        };

        selectResultado?.addEventListener(
          "change",
          actualizarMotivos
        );

        actualizarMotivos();
      },
      preConfirm: () => {
        const resultado = document.getElementById(
          "swal-resultado-masivo"
        )?.value;

        const observacion = document
          .getElementById("swal-observacion-masiva")
          ?.value?.trim();

        const motivosNoApto = document
          .getElementById("swal-motivos-no-apto")
          ?.value?.trim();

        if (!resultado) {
          Swal.showValidationMessage(
            "Seleccioná APTO o NO APTO."
          );
          return false;
        }

        if (resultado === "no_apto" && !motivosNoApto) {
          Swal.showValidationMessage(
            "Para NO APTO debés ingresar los motivos."
          );
          return false;
        }

        return {
          resultado,
          observacion: observacion || "",
          motivos:
            resultado === "no_apto" && motivosNoApto
              ? [motivosNoApto]
              : [],
        };
      },
    });

    if (!formulario) return;

    const estado =
      formulario.resultado === "apto"
        ? "aprobado"
        : "rechazado";

    const textoResultado =
      formulario.resultado === "apto"
        ? "APTO"
        : "NO APTO";

    const evaluacionesPayload = seleccionadas
      .map(({ personaProceso, instancia, item }) => {
        const persona = personaProceso?.persona || {};

        return {
          persona_id:
            persona?.dni ||
            personaProceso?.persona_id ||
            persona?.id ||
            null,
          dni:
            persona?.dni ||
            personaProceso?.persona_id ||
            null,
          item_id: getItemIdReal(item),
          proceso_id:
            etapa?.proceso_id ||
            personaProceso?.proceso_id ||
            item?.proceso_id ||
            null,
          proceso_etapa_id: etapa?.id || null,
          proceso_etapa_instancia_id:
            item?.proceso_etapa_instancia_id ||
            instancia?.id ||
            null,
          titulo_item:
            item?.descripcion ||
            item?.nombre ||
            "Evaluación",
        };
      })
      .filter(
        (evaluacion) =>
          evaluacion.persona_id &&
          evaluacion.item_id &&
          evaluacion.proceso_id
      );

    if (!evaluacionesPayload.length) {
      Swal.fire(
        "Error",
        "Las evaluaciones no tienen identificadores válidos.",
        "error"
      );
      return;
    }

    const confirmado = await Swal.fire({
      title: "¿Confirmar calificación masiva?",
      html: `
        <div style="text-align:left">
          <p>
            Se calificarán <b>${evaluacionesPayload.length}</b>
            evaluación(es) como <b>${textoResultado}</b>.
          </p>

          ${
            formulario.resultado === "no_apto"
              ? `
                <p>
                  <b>Motivos de NO APTO:</b>
                  ${formulario.motivos.join(", ")}
                </p>
              `
              : ""
          }

          ${
            formulario.observacion
              ? `
                <p>
                  <b>Observación:</b>
                  ${formulario.observacion}
                </p>
              `
              : ""
          }
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor:
        estado === "aprobado" ? "#16a34a" : "#dc2626",
    });

    if (!confirmado.isConfirmed) return;

    const usuarioNombre = Usuario
      ? `${Usuario.identificacion || ""} ${
          Usuario.apellido || ""
        }, ${Usuario.nombre || ""}`.trim()
      : "sistema";

    const payload = {
      evaluaciones: evaluacionesPayload,
      proceso_id: etapa?.proceso_id || null,
      estado,
      resultado: textoResultado,
      observacion: formulario.observacion,
      motivos:
        formulario.resultado === "no_apto"
          ? formulario.motivos
          : [],
      usuario_id:
        Usuario?.id ||
        Usuario?.usuarioID ||
        Usuario?.identificacion ||
        null,
      usuario_nombre: usuarioNombre,
    };

//     console.log(
//       "PAYLOAD MASIVO RECLUTAMIENTO:",
//       JSON.stringify(payload, null, 2)
//     );

    try {
      setGuardandoCalificacionMasiva(true);

      Swal.fire({
        title: "Guardando evaluaciones",
        html: `Procesando ${evaluacionesPayload.length} registro(s)...`,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const response = await axios.post(
        `${API}/togglePersonaItemMasivo`,
        payload
      );

      const respuesta = response.data || {};
      const resumen = respuesta.resumen || {};

      const guardadas =
        Number(resumen.guardadas) ||
        respuesta.guardados?.length ||
        0;

      const cantidadErrores =
        Number(resumen.errores) ||
        respuesta.errores?.length ||
        0;

      aplicarGuardadosMasivosEnPantalla(
        respuesta.guardados || []
      );

      await fetchData({ silencioso: true });

      if (cantidadErrores === 0) {
        limpiarSeleccionEvaluaciones();

        await Swal.fire(
          "Calificación guardada",
          `${guardadas || evaluacionesPayload.length} evaluación(es) fueron calificadas como ${textoResultado}.`,
          "success"
        );

        return;
      }

      const clavesFallidas = new Set(
        (respuesta.errores || []).map(
          (error) =>
            `${error.persona_id}-${error.item_id}`
        )
      );

      setEvaluacionesSeleccionadas((prev) => {
        const siguiente = {};

        Object.entries(prev).forEach(([clave, registro]) => {
          const personaId =
            registro?.personaProceso?.persona?.dni ||
            registro?.personaProceso?.persona_id ||
            registro?.personaProceso?.persona?.id;

          const itemId = getItemIdReal(registro?.item);

          if (
            clavesFallidas.has(
              `${personaId}-${itemId}`
            )
          ) {
            siguiente[clave] = registro;
          }
        });

        return siguiente;
      });

      const erroresHtml = (respuesta.errores || [])
        .map(
          (error) => `
            <div style="margin-bottom:6px">
              <b>${error.dni || error.persona_id || "Persona"}</b>
              ${error.item_id ? ` - Item ${error.item_id}` : ""}
              <br/>
              ${error.error || "Error desconocido"}
            </div>
          `
        )
        .join("");

      await Swal.fire({
        title: "Proceso finalizado con observaciones",
        icon: "warning",
        html: `
          <div style="text-align:left">
            <p><b>Guardadas:</b> ${guardadas}</p>
            <p><b>Fallidas:</b> ${cantidadErrores}</p>
            <div style="max-height:250px;overflow:auto">
              ${erroresHtml}
            </div>
          </div>
        `,
      });
    } catch (error) {
      console.error(
        "Error calificación masiva:",
        error
      );

      Swal.fire(
        "Error",
        error?.response?.data?.detalle ||
          error?.response?.data?.error ||
          error?.response?.data?.mensaje ||
          error?.message ||
          "No se pudo realizar la calificación masiva.",
        "error"
      );
    } finally {
      setGuardandoCalificacionMasiva(false);
    }
  };

  // =====================================================
  // RESUMEN GENERAL APTO / NO APTO / PENDIENTE
  // =====================================================
  const resumenAptitud = useMemo(() => {
    const aptos = [];
    const noAptos = [];
    const pendientes = [];

    dataLocal.forEach((fila) => {
      const resumen = getResultadoGeneralPersona(fila);
      const registro = {
        ...fila,
        resultadoGeneral: resumen.resultado,
        resumenAptitud: resumen,
      };

      if (resumen.resultado === "APTO") aptos.push(registro);
      else if (resumen.resultado === "NO_APTO") noAptos.push(registro);
      else pendientes.push(registro);
    });

    return {
      aptos,
      noAptos,
      pendientes,
      cantidadAptos: aptos.length,
      cantidadNoAptos: noAptos.length,
      cantidadPendientes: pendientes.length,
    };
  }, [dataLocal]);

const descargarExcelAptitud = () => {
  // Se exporta el padrón completo, no sólo la página o el filtro visible.
  const registros = dataLocal;
  const rotulo = (valor) => valor === "NO_APTO" ? "NO APTO" : valor === "APTO" ? "APTO" : valor === "SIN_EVALUACION" ? "SIN EVALUACIÓN" : "PENDIENTE";
  const texto = (valor) => valor === null || valor === undefined ? "" : String(valor);
  const nombreInstancia = (inst) => inst?.nombre || `Instancia ${inst?.id ?? ""}`;
  const nombreItem = (item) => getNombreItem(item);
  const notaNumerica = (item) => {
    if (!configNota(item)) return "";
    const valor = texto(item?.resultado).trim().replace(",", ".");
    return valor !== "" && Number.isFinite(Number(valor)) ? Number(valor) : "";
  };
  const reglaNumerica = (item) => {
    if (!configNota(item)) return "";
    const obs = texto(item?.observacion).trim();
    if (/^numerico\s*(?:(?:>=|<=|>|<|=)\s*-?\d+(?:[.,]\d+)?)?$/i.test(obs)) return obs;
    const cfg = item?.configuracionNumerica;
    if (cfg?.operador && cfg?.umbral !== undefined && cfg?.umbral !== "") return `numerico${cfg.operador}${cfg.umbral}`;
    return "numerico";
  };
  const estadoLegible = (item) => {
    const estado = normalizar(item?.estado);
    const resultado = normalizar(item?.resultado).replace(/-/g, "_");
    if (estado === "rechazado" || resultado === "no apto" || resultado === "no_apto") return "NO APTO";
    if (estado === "aprobado" || resultado === "apto") return "APTO";
    if (estado === "cargado" || estado === "en_progreso") return "CARGADO";
    if (estado === "completado") return "COMPLETADO";
    return estado && estado !== "pendiente" ? estado.toUpperCase() : "PENDIENTE";
  };

  // Claves estables: incluyen IDs para evitar colisiones entre ítems homónimos.
  const columnasItems = new Map();
  registros.forEach(fila => fila?.instancias?.forEach(inst => inst?.items?.forEach(item => {
    const clave = `${inst?.id ?? nombreInstancia(inst)}-${getItemIdReal(item) ?? nombreItem(item)}`;
    if (!columnasItems.has(clave)) columnasItems.set(clave, {
      clave,
      titulo: `${nombreInstancia(inst)} / ${nombreItem(item)} [${getItemIdReal(item) ?? "s/id"}]`,
      numerico: Boolean(configNota(item))
    });
  })));

  const resumenFilas = [];
  const detalleFilas = [];
  const instanciasConocidas = new Map();
  registros.forEach(fila => fila?.instancias?.forEach(inst => {
    if (!instanciasConocidas.has(String(inst?.id ?? nombreInstancia(inst)))) {
      instanciasConocidas.set(String(inst?.id ?? nombreInstancia(inst)), nombreInstancia(inst));
    }
  }));

  registros.forEach(fila => {
    const persona = fila?.persona || {};
    const resumen = getResultadoGeneralPersona(fila);
    const base = {
      DNI: persona.dni ?? "",
      Apellido: persona.apellido ?? "",
      Nombre: persona.nombre ?? "",
      Resultado_General: rotulo(resumen.resultado),
    };
    const filaResumen = {
      ...base,
      Instancias_Aptas: resumen.aptas,
      Instancias_No_Aptas: resumen.noAptas,
      Instancias_Pendientes: resumen.pendientes,
    };
    const mapaInstancias = new Map((resumen.detalleInstancias || []).map(inst => [inst.nombre, rotulo(inst.resultado)]));
    instanciasConocidas.forEach(nombre => { filaResumen[`Instancia: ${nombre}`] = mapaInstancias.get(nombre) || "SIN EVALUACIÓN"; });

    const motivos = [];
    const valoresItems = new Map();
    fila?.instancias?.forEach(inst => inst?.items?.forEach(item => {
      const clave = `${inst?.id ?? nombreInstancia(inst)}-${getItemIdReal(item) ?? nombreItem(item)}`;
      const nota = notaNumerica(item);
      const regla = reglaNumerica(item);
      const estado = estadoLegible(item);
      const resultado = texto(item?.resultado);
      const esNumerico = Boolean(configNota(item));
      valoresItems.set(clave, { nota, regla, estado, resultado, esNumerico });
      if (esEvaluacion(item) && estado === "NO APTO") motivos.push(`${nombreInstancia(inst)}: ${nombreItem(item)}${nota !== "" ? ` (nota ${nota})` : ""}`);
      detalleFilas.push({
        ...base,
        Instancia: nombreInstancia(inst),
        Instancia_ID: inst?.id ?? "",
        Item: nombreItem(item),
        Item_ID: getItemIdReal(item) ?? "",
        Tipo: texto(item?.tipo),
        Estado: estado,
        Estado_Interno: texto(item?.estado),
        Resultado: resultado,
        Nota: nota,
        Regla_Numerica: regla,
        Texto_Aprobado: texto(item?.texto_aprobado),
        Texto_Rechazado: texto(item?.texto_rechazado),
        Observacion_Configuracion: texto(item?.observacion),
        Cantidad_Intervenciones: item?.cantidadIntervenciones ?? "",
        Fecha_Intervencion: texto(item?.fechaIntervencion),
      });
    }));
    filaResumen.Detalle_No_Apto = [...new Set(motivos)].join(" | ");
    columnasItems.forEach(({ clave, titulo, numerico }) => {
      const dato = valoresItems.get(clave);
      filaResumen[`${titulo} - Estado`] = dato?.estado ?? "SIN REGISTRO";
      filaResumen[`${titulo} - Resultado`] = dato?.resultado ?? "";
      if (numerico) {
        filaResumen[`${titulo} - Nota`] = dato?.nota ?? "";
        filaResumen[`${titulo} - Regla`] = dato?.regla ?? "";
      }
    });
    resumenFilas.push(filaResumen);
  });

  const wb = XLSX.utils.book_new();
  const wsResumen = XLSX.utils.json_to_sheet(resumenFilas);
  const wsDetalle = XLSX.utils.json_to_sheet(detalleFilas);
  const ancho = (hoja, datos) => {
    const columnas = datos.length ? Object.keys(datos[0]) : [];
    hoja["!cols"] = columnas.map(col => ({ wch: Math.min(58, Math.max(14, col.length + 2)) }));
    hoja["!autofilter"] = { ref: hoja["!ref"] || "A1" };
  };
  ancho(wsResumen, resumenFilas);
  ancho(wsDetalle, detalleFilas);
  XLSX.utils.book_append_sheet(wb, wsResumen, "Resumen aptitud");
  XLSX.utils.book_append_sheet(wb, wsDetalle, "Detalle de items");
  const nombreArchivo = String(etapa?.nombre || "etapa")
    .replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ]+/g, "_")
    .replace(/^_+|_+$/g, "");
  XLSX.writeFile(wb, `aptos_no_aptos_${nombreArchivo}.xlsx`);
};
  const resumenPorInstancia = useMemo(() => {
    const resumen = {};

    filteredData.forEach((p) => {
      p.instancias?.forEach((inst) => {
        if (!resumen[inst.id]) {
          resumen[inst.id] = {
            aprobado: 0,
            pendiente: 0,
            en_progreso: 0,
            nombre: inst.nombre,
            items: {},
          };
        }

        inst.items?.forEach((item) => {
          const estadoNormalizado = getEstadoNormalizadoItem(item);

          if (estadoNormalizado === "completado") resumen[inst.id].aprobado++;
          else if (estadoNormalizado === "en_progreso")
            resumen[inst.id].en_progreso++;
          else resumen[inst.id].pendiente++;

          const nombreItem = getNombreItem(item);

          if (!resumen[inst.id].items[nombreItem]) {
            resumen[inst.id].items[nombreItem] = {
              aprobado: 0,
              pendiente: 0,
              en_progreso: 0,
            };
          }

          if (estadoNormalizado === "completado") {
            resumen[inst.id].items[nombreItem].aprobado++;
          } else if (estadoNormalizado === "en_progreso") {
            resumen[inst.id].items[nombreItem].en_progreso++;
          } else {
            resumen[inst.id].items[nombreItem].pendiente++;
          }
        });
      });
    });

    return resumen;
  }, [filteredData]);

  const resumenPresentes = useMemo(() => {
    if (esPerfeccionamiento) {
      return {
        totalPersonas: 0,
        totalRegistros: 0,
        porFecha: {},
        porDependencia: {},
      };
    }

    let totalPersonas = 0;
    let totalRegistros = 0;

    const porFecha = {};
    const porDependencia = {};

    dataLocal.forEach((p) => {
      const presentesTodos = getPresentesPersona(p);

      const presentes =
        filtroSede === "todos"
          ? presentesTodos
          : presentesTodos.filter(
              (presente) =>
                String(presente?.sedeID || "") ===
                String(filtroSede)
            );

      if (presentes.length > 0) {
        totalPersonas++;
      }

      // Una persona cuenta una sola vez por fecha, aunque haya
      // recibido presentes de varias dependencias el mismo día.
      const fechasPersona = new Set();

      presentes.forEach((presente) => {
        totalRegistros++;

        const fecha = presente?.fecha || "Sin fecha";
        fechasPersona.add(fecha);

        const dependencia = String(
          presente?.dependencia || "Sin dependencia"
        ).trim();

        if (!porDependencia[dependencia]) {
          porDependencia[dependencia] = 0;
        }

        porDependencia[dependencia]++;
      });

      fechasPersona.forEach((fecha) => {
        if (!porFecha[fecha]) {
          porFecha[fecha] = 0;
        }

        porFecha[fecha]++;
      });
    });

    return {
      totalPersonas,
      totalRegistros,
      porFecha,
      porDependencia,
    };
  }, [
    dataLocal,
    esPerfeccionamiento,
    presentesPorDni,
    filtroSede,
  ]);

  const resumenGeneros = useMemo(() => {
    const resumen = {};

    filteredData.forEach((p) => {
      const genero = p.persona?.genero || p.persona?.sexo || "Sin género";

      if (!resumen[genero]) {
        resumen[genero] = {
          matriculados: 0,
          presentes: 0,
        };
      }

      resumen[genero].matriculados++;

      if (
        !esPerfeccionamiento &&
        getPresentesPersona(p).length > 0
      ) {
        resumen[genero].presentes++;
      }
    });

    return resumen;
  }, [filteredData, esPerfeccionamiento, presentesPorDni]);

  const abrirFormulario = async (item) => {
    try {
      const res = await axios.post(`${API}/obtenerDeclaracionJurada`, {
        dni: item.dni || item.persona_dni || null,
        persona_id: item.persona_id,
        formulario_id: item.formulario_id,
      });
//       console.log(
//         "DECLARACION JURADA COMPLETA",
//         JSON.stringify(res.data, null, 2)
//       );
      generarPDFDeclaracionJurada(res.data);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo abrir el formulario", "error");
    }
  };

  const abroExamenSanidad = (persona, instancia, itemId) => {
    localStorage.setItem("dniFormulario", persona.dni.toString());
    localStorage.setItem(
      "apellidoNombre",
      `${persona.apellido} ${persona.nombre}`
    );
    localStorage.setItem(
      "usuarioGuardo",
      Usuario?.identificacion?.toString() || ""
    );
    localStorage.setItem("etapa", instancia.toString());
    localStorage.setItem("item", itemId.toString());

    window.open(`/SanidadFormulario`, "_blank");
  };

  const descargarSCL90 = async (
    persona,
    etapaPsico = "Auxiliar Forense 2026"
  ) => {
    try {
      const dni = persona.dni;

      const response = await axios.post(
        "https://wwwp.isepsantafe.net.ar/psicologia/pdf_respuestas_scl90",
        {
          dni,
          etapa: etapaPsico,
        },
        {
          responseType: "blob",
          headers: {
            Authorization:
              "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c3VhcmlvIjoibGVhbmRybyJ9.XprcUoRssqDYD-gFEJ1j5F60bXWaPK4fzO_56yZ4aqw",
          },
        }
      );

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const nuevaVentana = window.open("", "_blank");

      if (!nuevaVentana) {
        Swal.fire(
          "Atención",
          "El navegador bloqueó la ventana emergente",
          "warning"
        );
        return;
      }

      nuevaVentana.document.write(`
          <html>
            <head>
              <title>SCL90_${dni}</title>
              <style>
                html, body {
                  margin: 0;
                  height: 100%;
                }

                iframe {
                  width: 100%;
                  height: 100%;
                  border: none;
                }
              </style>
            </head>

            <body>
              <iframe src="${url}"></iframe>
            </body>
          </html>
        `);

      nuevaVentana.document.close();
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo abrir el PDF SCL90", "error");
    }
  };

  const toggleItem = async (personaDni, instanciaId, itemId) => {
    const personaFila = dataLocal.find((p) => p.persona.dni === personaDni);
    const persona = personaFila?.persona;
    const instancia = personaFila?.instancias?.find(
      (i) => i.id === instanciaId
    );
    const item = instancia?.items?.find((it) => it.id === itemId);

    if (!item || item.tipo !== "archivo") return;

    const estadoAnterior = item.estado;
    const nuevoEstado =
      estadoAnterior === "aprobado" ? "pendiente" : "aprobado";

    const result = await Swal.fire({
      title: "Archivo",
      html: `
          <div style="text-align:left">
            <b>Item:</b><br/>
            ${getNombreItem(item)}<br/><br/>
            Elegí qué querés hacer:
          </div>
        `,
      icon: "question",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText:
        nuevoEstado === "aprobado" ? "✅ Marcar completado" : "↩ Quitar completado",
      denyButtonText: "📂 Ver archivo",
      cancelButtonText: "Cancelar",
    });

    if (result.isDenied) {
      abrirArchivoItem(persona, instancia.id, item);
      return;
    }

    if (!result.isConfirmed) return;

    setDataLocal((prev) =>
      prev.map((p) => {
        if (p.persona.dni !== personaDni) return p;

        return {
          ...p,
          instancias: p.instancias.map((inst) => {
            if (inst.id !== instanciaId) return inst;

            return {
              ...inst,
              items: inst.items.map((it) =>
                it.id === itemId ? { ...it, estado: nuevoEstado } : it
              ),
            };
          }),
        };
      })
    );

    axios
      .post(`${API}/togglePersonaItem`, {
        persona_id: personaDni,
        item_id: itemId,
        proceso_id: etapa.proceso_id || 1,
        estado: nuevoEstado,
        tipo: "archivo",
        observaciones: "Visado manual " + Usuario?.identificacion,
        usuario_id: Usuario.identificacion,
usuario_nombre: `${Usuario.apellido}, ${Usuario.nombre}`,
      })
      .catch(() => {
        setDataLocal((prev) =>
          prev.map((p) => {
            if (p.persona.dni !== personaDni) return p;

            return {
              ...p,
              instancias: p.instancias.map((inst) => {
                if (inst.id !== instanciaId) return inst;

                return {
                  ...inst,
                  items: inst.items.map((it) =>
                    it.id === itemId ? { ...it, estado: estadoAnterior } : it
                  ),
                };
              }),
            };
          })
        );

        Swal.fire("Error", "No se pudo guardar", "error");
      });
  };

  const handleClickItem = (item, persona, inst) => {
    if (configNota(item)) return;
    if (esPerfeccionamiento && item.tipo === "evaluacion") {
      abrirEvaluacionPerfeccionamiento(persona, inst.id, item);
      return;
    }

    if (item.tipo === "archivo") {
      toggleItem(persona.dni, inst.id, item.id);
      return;
    }

    if (item.tipo === "evaluacion" && item.observacion === "Carpeta Medica") {
      abrirModal(persona, inst.id, item, "evaluacion_medica");
      return;
    }

    if (item.tipo === "evaluacion" && item.observacion === "SCL90") {
      descargarSCL90(persona);
      return;
    }

    if (
      item.tipo === "evaluacion" &&
      item.observacion !== "Carpeta Medica" &&
      item.observacion !== "Preocupacional" &&
      item.observacion !== "Perfeccionamiento"
    ) {
      abrirModal(persona, inst.id, item, "psicologia");
      return;
    }

    if (
      item.tipo === "evaluacion" &&
      (item.observacion === "Preocupacional" ||
        item.observacion === "Perfeccionamiento")
    ) {
      abroExamenSanidad(persona, inst.id, item.id);
      return;
    }

    if (item.tipo === "formulario") {
      abrirFormulario({
        ...item,
        dni: persona.dni,
        persona_id: persona.id,
      });
    }
  };

  const mostrarPresentes = (presentes = []) => {
    if (
      esPerfeccionamiento ||
      !Array.isArray(presentes) ||
      presentes.length === 0
    ) {
      return;
    }

    const html = presentes
      .map((presente) => {
        const dependencia =
          presente?.dependencia || "Sin dependencia";

        const color =
          getColorDependencia(dependencia);

        const colorTexto =
          getColorTextoDependencia(dependencia);

        return `
          <div
            style="
              text-align:left;
              padding:12px;
              margin-bottom:10px;
              border-radius:10px;
              background:${color};
              color:${colorTexto};
            "
          >
            <div
              style="
                font-weight:800;
                margin-bottom:6px;
              "
            >
              ✔ ${dependencia}
            </div>

            <div><b>Sede:</b> ${getNombreSede(presente?.sedeID)}</div>
            <div><b>Fecha:</b> ${presente?.fecha || "—"}</div>
            <div><b>Hora:</b> ${presente?.hora || "—"}</div>
            <div><b>Turno:</b> ${getTurno(presente?.hora)}</div>
            <div><b>Usuario:</b> ${presente?.usuario || "—"}</div>
          </div>
        `;
      })
      .join("");

    Swal.fire({
      title:
        presentes.length === 1
          ? "Presente registrado"
          : `Presentes registrados (${presentes.length})`,
      html: `
        <div style="max-height:450px;overflow:auto;padding-right:4px">
          ${html}
        </div>
      `,
      icon: "info",
      width: 700,
      confirmButtonText: "Cerrar",
    });
  };

  const mostrarLoadingExcel = (titulo = "GENERANDO ARCHIVO EXCEL") => {
    Swal.fire({
      title: `
        <div style="
          color:#ffffff;
          font-size:20px;
          font-weight:900;
          letter-spacing:3px;
        ">
          ${titulo}
        </div>
      `,
      html: `
        <div style="
          padding:10px 5px 5px;
          text-align:center;
        ">
          <div style="
            margin:12px 0;
            font-size:56px;
            animation: excelFlotar 1.8s ease-in-out infinite;
          ">
            📊
          </div>

          <div style="
            color:#dcecff;
            font-size:16px;
            font-weight:700;
            margin-top:8px;
          ">
            Preparando información...
            <span id="excel-loading-puntos"></span>
          </div>

          <div style="
            margin-top:16px;
            height:9px;
            overflow:hidden;
            border-radius:999px;
            background:rgba(255,255,255,.10);
          ">
            <div style="
              width:45%;
              height:100%;
              border-radius:999px;
              background:linear-gradient(90deg,#4dabf7,#63e6be);
              animation: excelBarra 1.2s ease-in-out infinite alternate;
            "></div>
          </div>

          <div style="
            margin-top:18px;
            color:#8fb9df;
            font-size:11px;
            font-weight:700;
            letter-spacing:1.5px;
            text-transform:uppercase;
          ">
            No cierre esta ventana
          </div>
        </div>

        <style>
          .swal2-popup.excel-loading-popup {
            width:500px;
            padding:28px;
            border-radius:22px;
            background:
              radial-gradient(circle at top,rgba(54,89,161,.35),transparent 45%),
              linear-gradient(145deg,#081120,#111f35);
            box-shadow:
              0 0 40px rgba(54,89,161,.35),
              0 30px 80px rgba(0,0,0,.5);
          }

          @keyframes excelFlotar {
            0%,100% { transform:translateY(0) scale(1); }
            50% { transform:translateY(-8px) scale(1.06); }
          }

          @keyframes excelBarra {
            from { transform:translateX(-30%); }
            to { transform:translateX(150%); }
          }
        </style>
      `,
      customClass: {
        popup: "excel-loading-popup",
      },
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        let puntos = 0;

        window.__excelLoadingInterval = setInterval(() => {
          puntos = (puntos + 1) % 4;

          const elemento =
            Swal.getHtmlContainer()?.querySelector(
              "#excel-loading-puntos"
            );

          if (elemento) {
            elemento.textContent = ".".repeat(puntos);
          }
        }, 400);
      },
      willClose: () => {
        if (window.__excelLoadingInterval) {
          clearInterval(window.__excelLoadingInterval);
          window.__excelLoadingInterval = null;
        }
      },
    });
  };

  const cerrarLoadingExcel = () => {
    if (window.__excelLoadingInterval) {
      clearInterval(window.__excelLoadingInterval);
      window.__excelLoadingInterval = null;
    }

    Swal.close();
  };

  const exportToExcel = async () => {
    mostrarLoadingExcel("GENERANDO EXCEL COMPLETO");

    // Dejamos que SweetAlert se pinte antes de comenzar.
    await new Promise((resolve) => setTimeout(resolve, 150));

    try {
      // =====================================================
      // IMPORTANTE:
      // UNA SOLA FILA POR PERSONA.
      //
      // Antes generábamos una fila por persona x instancia x item
      // x archivo y después duplicábamos esas filas en muchas hojas.
      // Con miles de inscriptos el libro podía crecer hasta provocar
      // RangeError: Invalid array length.
      // =====================================================

      // IMPORTANTE:
      // El Excel completo se genera sobre TODOS los matriculados
      // de la etapa, no sobre filteredData.
      //
      // filteredData puede estar reducido por:
      // - pendientes/evaluados
      // - fecha de asistencia
      // - sede
      // - turno
      // - estado/item
      // - búsqueda
      //
      // Si usáramos filteredData, la hoja "Con presente" no
      // coincidiría con el contador real de presentes.
      const filasPersonas = dataLocal.map((p) => {
        const persona = p?.persona || {};

        const presentes = esPerfeccionamiento
          ? []
          : getPresentesPersona(p);

        const sedes = esPerfeccionamiento
          ? []
          : getSedesPersona(p);

        const items =
          p?.instancias?.flatMap(
            (instancia) =>
              (instancia?.items || []).map((item) => ({
                ...item,
                __instancia:
                  instancia?.nombre || "",
              }))
          ) || [];

        let completados = 0;
        let enProceso = 0;
        let pendientes = 0;
        let rechazados = 0;
        let aptos = 0;
        let noAptos = 0;

        items.forEach((item) => {
          const estado =
            normalizar(
              getEstadoNormalizadoItem(item)
            );

          const estadoOriginal =
            normalizar(item?.estado);

          const resultado =
            normalizar(item?.resultado);

          if (
            estado === "completado" ||
            estadoOriginal === "aprobado"
          ) {
            completados++;
          } else if (
            estado === "en_progreso" ||
            estadoOriginal === "cargado"
          ) {
            enProceso++;
          } else {
            pendientes++;
          }

          if (
            estadoOriginal === "rechazado" ||
            resultado === "no apto" ||
            resultado === "no_apto"
          ) {
            rechazados++;
          }

          if (
            resultado === "apto" ||
            resultado === "aprobado"
          ) {
            aptos++;
          }

          if (
            resultado === "no apto" ||
            resultado === "no_apto"
          ) {
            noAptos++;
          }
        });

        let estadoGeneral = "Pendiente";

        if (rechazados > 0 || noAptos > 0) {
          estadoGeneral = "Rechazado";
        } else if (
          items.length > 0 &&
          completados === items.length
        ) {
          estadoGeneral = "Aprobado";
        } else if (
          completados > 0 ||
          enProceso > 0 ||
          aptos > 0
        ) {
          estadoGeneral = "En proceso";
        }

        const detallePresentes = presentes
          .map(
            (presente) =>
              `${
                presente?.dependencia ||
                "Sin dependencia"
              } | ${
                presente?.fecha || ""
              } | ${
                presente?.hora || ""
              } | ${
                getNombreSede(
                  presente?.sedeID
                )
              } | ${
                presente?.usuario || ""
              }`
          )
          .join(" || ");

        return {
          DNI: persona?.dni || "",
          Apellido: persona?.apellido || "",
          Nombre: persona?.nombre || "",
          Genero:
            persona?.genero ||
            persona?.sexo ||
            "",

          EstadoGeneral: estadoGeneral,

          TotalItems: items.length,
          Completados: completados,
          EnProceso: enProceso,
          Pendientes: pendientes,
          Rechazados: rechazados,
          Aptos: aptos,
          NoAptos: noAptos,

          ...(esPerfeccionamiento
            ? {}
            : {
                TienePresente:
                  presentes.length > 0
                    ? "SI"
                    : "NO",

                CantidadPresentes:
                  presentes.length,

                SedesPresentes:
                  sedes.join(" | "),

                DependenciasPresentes:
                  Array.from(
                    new Set(
                      presentes
                        .map(
                          (presente) =>
                            presente?.dependencia ||
                            "Sin dependencia"
                        )
                        .filter(Boolean)
                    )
                  ).join(" | "),

                DetallePresentes:
                  detallePresentes,
              }),
        };
      });

      // =====================================================
      // GENERADOR DE HOJAS
      // =====================================================
      const agregarHoja = (
        workbook,
        nombre,
        filas
      ) => {
        const datos =
          Array.isArray(filas) &&
          filas.length > 0
            ? filas
            : [
                {
                  Informacion:
                    "No hay registros para esta hoja",
                },
              ];

        const hoja =
          XLSX.utils.json_to_sheet(datos);

        const columnas =
          Object.keys(datos[0] || {});

        hoja["!cols"] = columnas.map(
          (columna) => {
            if (
              columna === "DetallePresentes"
            ) {
              return { wch: 60 };
            }

            if (
              columna === "Apellido" ||
              columna === "Nombre"
            ) {
              return { wch: 25 };
            }

            if (
              columna ===
                "DependenciasPresentes" ||
              columna === "SedesPresentes"
            ) {
              return { wch: 35 };
            }

            return {
              wch: Math.min(
                Math.max(
                  String(columna).length + 2,
                  12
                ),
                22
              ),
            };
          }
        );

        XLSX.utils.book_append_sheet(
          workbook,
          hoja,
          nombre.substring(0, 31)
        );
      };

      const tieneSede = (
        fila,
        nombreSede
      ) =>
        String(
          fila?.SedesPresentes || ""
        )
          .split("|")
          .map((valor) => valor.trim())
          .includes(nombreSede);

      const wb = XLSX.utils.book_new();

      // =====================================================
      // GENERAL
      // =====================================================
      agregarHoja(
        wb,
        "Todos",
        filasPersonas
      );

      // =====================================================
      // POR SEDE
      // =====================================================
      if (!esPerfeccionamiento) {
        agregarHoja(
          wb,
          "Provincial",
          filasPersonas.filter((fila) =>
            tieneSede(
              fila,
              "Provincial"
            )
          )
        );

        agregarHoja(
          wb,
          "Rosario",
          filasPersonas.filter((fila) =>
            tieneSede(
              fila,
              "Rosario"
            )
          )
        );

        agregarHoja(
          wb,
          "Recreo",
          filasPersonas.filter((fila) =>
            tieneSede(
              fila,
              "Recreo"
            )
          )
        );

        agregarHoja(
          wb,
          "Sin sede",
          filasPersonas.filter(
            (fila) =>
              !fila.SedesPresentes ||
              fila.SedesPresentes ===
                "Sin sede"
          )
        );
      }

      // =====================================================
      // POR ESTADO GENERAL DE LA PERSONA
      // =====================================================
      agregarHoja(
        wb,
        "Aprobados",
        filasPersonas.filter(
          (fila) =>
            fila.EstadoGeneral ===
            "Aprobado"
        )
      );

      agregarHoja(
        wb,
        "En proceso",
        filasPersonas.filter(
          (fila) =>
            fila.EstadoGeneral ===
            "En proceso"
        )
      );

      agregarHoja(
        wb,
        "Pendientes",
        filasPersonas.filter(
          (fila) =>
            fila.EstadoGeneral ===
            "Pendiente"
        )
      );

      agregarHoja(
        wb,
        "Rechazados",
        filasPersonas.filter(
          (fila) =>
            fila.EstadoGeneral ===
            "Rechazado"
        )
      );

      // =====================================================
      // POR ASISTENCIA
      // =====================================================
      if (!esPerfeccionamiento) {
        agregarHoja(
          wb,
          "Con presente",
          filasPersonas.filter(
            (fila) =>
              fila.TienePresente ===
              "SI"
          )
        );

        agregarHoja(
          wb,
          "Sin presente",
          filasPersonas.filter(
            (fila) =>
              fila.TienePresente !==
              "SI"
          )
        );
      }

      // =====================================================
      // ACTUALIZAR LOADING ANTES DE ESCRIBIR
      // =====================================================
      const mensaje =
        Swal.getHtmlContainer()?.querySelector(
          "#excel-loading-puntos"
        );

      if (mensaje) {
        mensaje.parentElement.firstChild.textContent =
          "Construyendo archivo";
      }

      await new Promise(
        (resolve) => setTimeout(resolve, 80)
      );

      // La compresión reduce considerablemente memoria/tamaño.
      const buffer = XLSX.write(
        wb,
        {
          bookType: "xlsx",
          type: "array",
          compression: true,
        }
      );

      saveAs(
        new Blob([buffer]),
        `etapa_${etapa.nombre}_completo.xlsx`
      );

      cerrarLoadingExcel();

      await Swal.fire({
        icon: "success",
        title: "Excel generado",
        html: `
          <b>${filasPersonas.length}</b>
          persona(s) exportadas correctamente.
        `,
        confirmButtonText: "Aceptar",
      });
    } catch (error) {
      console.error(
        "Error generando Excel:",
        error
      );

      cerrarLoadingExcel();

      await Swal.fire({
        icon: "error",
        title: "Error generando Excel",
        html: `
          No se pudo construir el archivo.<br/><br/>
          <small>${error?.message || "Error desconocido"}</small>
        `,
      });
    } finally {
      cerrarLoadingExcel();
    }
  };

  const exportPresentesExcel = async () => {
    if (esPerfeccionamiento) return;

    mostrarLoadingExcel("GENERANDO EXCEL DE PRESENTES");

    await new Promise((resolve) => setTimeout(resolve, 120));

    try {
      const flat = [];

    dataLocal.forEach((p) => {
      const presentes =
        getPresentesPersona(p);

      presentes.forEach((presente) => {
        flat.push({
          DNI: p.persona.dni,
          Nombre: p.persona.nombre,
          Apellido: p.persona.apellido,
          Genero:
            p.persona.genero ||
            p.persona.sexo ||
            "",
          Dependencia:
            presente?.dependencia || "",
          Sede:
            getNombreSede(
              presente?.sedeID
            ),
          SedeID:
            presente?.sedeID || "",
          Fecha:
            presente?.fecha || "",
          Hora:
            presente?.hora || "",
          Turno:
            getTurno(
              presente?.hora
            ),
          Usuario:
            presente?.usuario || "",
        });
      });
    });

    const agregarHoja = (
      workbook,
      nombre,
      filas
    ) => {
      const datos =
        filas.length > 0
          ? filas
          : [
              {
                Informacion:
                  "No hay registros para esta hoja",
              },
            ];

      const ws =
        XLSX.utils.json_to_sheet(datos);

      ws["!cols"] = Object.keys(
        datos[0]
      ).map((columna) => ({
        wch: Math.min(
          Math.max(
            String(columna).length + 2,
            12
          ),
          35
        ),
      }));

      XLSX.utils.book_append_sheet(
        workbook,
        ws,
        nombre.substring(0, 31)
      );
    };

    const wb = XLSX.utils.book_new();

    agregarHoja(
      wb,
      "Todos",
      flat
    );

    agregarHoja(
      wb,
      "Provincial",
      flat.filter(
        (fila) =>
          fila.Sede === "Provincial"
      )
    );

    agregarHoja(
      wb,
      "Rosario",
      flat.filter(
        (fila) =>
          fila.Sede === "Rosario"
      )
    );

    agregarHoja(
      wb,
      "Recreo",
      flat.filter(
        (fila) =>
          fila.Sede === "Recreo"
      )
    );

    agregarHoja(
      wb,
      "Sin sede",
      flat.filter(
        (fila) =>
          !fila.Sede ||
          fila.Sede === "Sin sede"
      )
    );

    // Una hoja por dependencia encontrada.
    const dependencias =
      Array.from(
        new Set(
          flat
            .map(
              (fila) =>
                fila.Dependencia
            )
            .filter(Boolean)
        )
      ).sort();

    dependencias.forEach(
      (dependencia) => {
        agregarHoja(
          wb,
          dependencia,
          flat.filter(
            (fila) =>
              fila.Dependencia ===
              dependencia
          )
        );
      }
    );

    const buffer = XLSX.write(
      wb,
      {
        bookType: "xlsx",
        type: "array",
      }
    );

      saveAs(
        new Blob([buffer]),
        `presentes_${etapa.nombre}_por_sede.xlsx`
      );
    } catch (error) {
      console.error(
        "Error generando Excel de presentes:",
        error
      );

      cerrarLoadingExcel();

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo generar el Excel de presentes.",
      });

      return;
    } finally {
      cerrarLoadingExcel();
    }
  };

  if (loading) return <p>Cargando...</p>;

  return (
    <div style={styles.page}>
      <style>{`
        .ise-correccion-toolbar {display:flex;align-items:center;flex-wrap:wrap;gap:10px;padding:9px 12px;margin:10px 0;background:#f1f5f9;border:1px solid #cbd5e1;border-radius:8px;font-size:13px}
        .ise-correccion-toolbar label {display:flex;align-items:center;gap:5px}
        .ise-correccion-toolbar button,.ise-correccion-toolbar select {padding:7px 10px;border:1px solid #94a3b8;border-radius:6px;background:white;cursor:pointer}
        .ise-tabla-compacta {overflow-x:auto;max-width:100%}
        .ise-tabla-compacta table {width:max-content;min-width:100%;border-collapse:collapse;font-size:11px}
        .ise-tabla-compacta th {position:sticky;top:0;z-index:2;background:#e2e8f0;white-space:nowrap;padding:5px 6px!important}
        .ise-tabla-compacta td {padding:4px 5px!important;vertical-align:middle!important;line-height:1.2}
        .ise-tabla-compacta td:nth-child(3),.ise-tabla-compacta th:nth-child(3) {position:sticky;left:0;z-index:1;background:#f8fafc;min-width:95px}
        .ise-tabla-compacta td:nth-child(4),.ise-tabla-compacta th:nth-child(4) {min-width:90px}
        .ise-tabla-compacta td button {font-size:10px;padding:4px 6px!important}
        .ise-tabla-compacta td input[type=text] {max-width:70px}
        .ise-tabla-compacta td>div {gap:3px!important}
        .ise-correccion-panel {border:1px solid #cbd5e1;border-radius:9px;background:white;max-width:900px;margin:10px auto;overflow:hidden}
        .ise-correccion-ayuda {padding:10px 14px;background:#eff6ff;color:#1e40af;font-size:12px}
        .ise-correccion-head,.ise-correccion-fila {display:grid;grid-template-columns:minmax(180px,1fr) minmax(190px,250px);gap:12px;align-items:center;padding:6px 14px;border-bottom:1px solid #e2e8f0}
        .ise-correccion-head {font-weight:bold;background:#f1f5f9;font-size:12px}
        .ise-correccion-fila:nth-child(even) {background:#f8fafc}
        .ise-correccion-fila .ise-correccion-nombre {display:flex;flex-direction:column;font-size:12px}
        .ise-correccion-fila small {font-size:10px;color:#64748b}
        .ise-correccion-fila input {width:90px!important}
        @media(max-width:540px){.ise-correccion-head,.ise-correccion-fila {grid-template-columns:1fr 165px;padding:6px;gap:5px}.ise-correccion-fila .ise-correccion-nombre {font-size:11px}}
      `}</style>
      <h4>📋 {etapa.nombre}</h4>

      <div style={styles.accionesDesmatriculacion}>
        <button
          type="button"
          onClick={() => setModalDesmatricularOpen(true)}
          style={styles.btnDesmatricularMasivo}
          disabled={desmatriculando}
        >
          🗑 Desmatricular masivamente
        </button>
      </div>

      {(filtroPresenteFecha ||
        filtroTurno ||
        filtroEstadoItem !== "todos" ||
        filtroItemNombre !== "todos" ||
        filtroFechaArchivoDesde) && (
          <div style={styles.filtrosActivos}>
            {!esPerfeccionamiento && filtroPresenteFecha && (
              <button
                onClick={() => {
                  setFiltroPresenteFecha(null);
                  setFiltroTipoAsistencia("todos");
                }}
                style={styles.btnFiltroActivo}
              >
                ❌ {filtroTipoAsistencia === "restantes" ? "Restan" : "Presentes"}{" "}
                {filtroPresenteFecha}
              </button>
            )}

            {!esPerfeccionamiento && filtroTurno && (
              <button
                onClick={() => setFiltroTurno(null)}
                style={styles.btnFiltroActivo}
              >
                ❌ Turno {filtroTurno}
              </button>
            )}

            {filtroItemNombre !== "todos" && (
              <button
                onClick={() => setFiltroItemNombre("todos")}
                style={styles.btnFiltroActivo}
              >
                ❌ Item {filtroItemNombre}
              </button>
            )}

            {filtroEstadoItem !== "todos" && (
              <button
                onClick={() => setFiltroEstadoItem("todos")}
                style={styles.btnFiltroActivo}
              >
                ❌ Estado {filtroEstadoItem}
              </button>
            )}

            {filtroFechaArchivoDesde && (
              <button
                onClick={() => setFiltroFechaArchivoDesde("")}
                style={styles.btnFiltroActivo}
              >
                ❌ Archivos desde{" "}
                {new Date(
                  `${filtroFechaArchivoDesde}T00:00:00`
                ).toLocaleDateString("es-AR")}
              </button>
            )}
          </div>
        )}

      {!esPerfeccionamiento && (
        <div style={styles.controlAsistenciaFecha}>
          <div style={styles.controlAsistenciaTitulo}>
            📅 Asistencia por fecha
          </div>

          <select
            value={filtroSede}
            onChange={(e) => {
              setFiltroSede(e.target.value);
              setPaginaActual(1);
            }}
            style={styles.selectSedeAsistencia}
            title="Filtrar asistencia por sede"
          >
            <option value="todos">Todas las sedes</option>
            <option value="1">Provincial</option>
            <option value="2">Rosario</option>
            <option value="3">Recreo</option>
          </select>

          <input
            type="date"
            value={filtroPresenteFecha || ""}
            onChange={(e) => {
              const fecha = e.target.value;

              if (!fecha) {
                setFiltroPresenteFecha(null);
                setFiltroTipoAsistencia("todos");
                return;
              }

              setFiltroPresenteFecha(fecha);
              setFiltroTipoAsistencia("presentes");
            }}
            style={styles.inputFechaAsistencia}
          />

          {filtroPresenteFecha && (
            <>
              <button
                type="button"
                onClick={() => setFiltroTipoAsistencia("presentes")}
                style={{
                  ...styles.btnAsistenciaFecha,
                  background:
                    filtroTipoAsistencia === "presentes"
                      ? "#16a34a"
                      : "#dcfce7",
                  color:
                    filtroTipoAsistencia === "presentes"
                      ? "#fff"
                      : "#166534",
                }}
              >
                ✔ Ver presentes
              </button>

              <button
                type="button"
                onClick={() => setFiltroTipoAsistencia("restantes")}
                style={{
                  ...styles.btnAsistenciaFecha,
                  background:
                    filtroTipoAsistencia === "restantes"
                      ? "#f59e0b"
                      : "#fef3c7",
                  color:
                    filtroTipoAsistencia === "restantes"
                      ? "#fff"
                      : "#92400e",
                }}
              >
                ⏳ Ver los que restan
              </button>

              <button
                type="button"
                onClick={() => {
                  setFiltroPresenteFecha(null);
                  setFiltroTipoAsistencia("todos");
                }}
                style={styles.btnLimpiarFechaAsistencia}
              >
                Limpiar
              </button>
            </>
          )}
        </div>
      )}

      {!esPerfeccionamiento && filtroSede !== "todos" && (
        <div style={styles.filtroSedeActivo}>
          <b>📍 Asistencia filtrada por sede:</b>{" "}
          {getNombreSede(filtroSede)}

          <button
            type="button"
            onClick={() => {
              setFiltroSede("todos");
              setPaginaActual(1);
            }}
            style={styles.btnLimpiarSede}
          >
            ✖ Todas las sedes
          </button>
        </div>
      )}

      {!esPerfeccionamiento && (
        <div style={styles.turnosContainer}>
          {[
            "Antes de 08:00",
            "08:00 - 09:59",
            "10:00 - 11:59",
            "12:00 - 13:59",
            "14:00 - 15:59",
            "16:00 - 17:59",
            "18:00 o más",
          ].map((t) => (
            <button
              key={t}
              onClick={() => setFiltroTurno((prev) => (prev === t ? null : t))}
              style={{
                ...styles.btnTurno,
                background: filtroTurno === t ? "#2563eb" : "#e5e7eb",
                color: filtroTurno === t ? "white" : "black",
              }}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      <div style={styles.filtroItemContainer}>
        <div style={styles.filtroFechaArchivo}>
          <label htmlFor="filtroFechaArchivoDesde" style={styles.labelFiltroFecha}>
            📅 Archivos subidos desde:
          </label>

          <input
            id="filtroFechaArchivoDesde"
            type="date"
            value={filtroFechaArchivoDesde}
            onChange={(e) => setFiltroFechaArchivoDesde(e.target.value)}
            style={styles.inputFechaArchivo}
          />

          {filtroFechaArchivoDesde && (
            <button
              type="button"
              onClick={() => setFiltroFechaArchivoDesde("")}
              style={styles.btnLimpiarFecha}
            >
              Limpiar fecha
            </button>
          )}
        </div>

        <select
          value={filtroItemNombre}
          onChange={(e) => setFiltroItemNombre(e.target.value)}
          style={styles.selectFiltro}
        >
          <option value="todos">Todos los items</option>

          {itemsDisponibles.map((nombre) => (
            <option key={nombre} value={nombre}>
              {nombre}
            </option>
          ))}
        </select>

        <select
          value={filtroEstadoItem}
          onChange={(e) => setFiltroEstadoItem(e.target.value)}
          style={styles.selectFiltro}
        >
          <option value="todos">Todos los estados</option>
          <option value="completado">Completados</option>
          <option value="en_progreso">En proceso</option>
          <option value="pendiente">Pendientes</option>
        </select>

        <button
          onClick={() => {
            setFiltroItemNombre("todos");
            setFiltroEstadoItem("todos");
            setFiltroFechaArchivoDesde("");
          }}
          style={styles.btnFiltroActivo}
        >
          Limpiar filtros
        </button>
      </div>


      <div style={styles.barraAptitudGeneral}>
        <div style={styles.tituloAptitudGeneral}>
          🎯 Resultado general por instancias
        </div>

        <button
          type="button"
          onClick={() => setFiltroAptitudGeneral("aptos")}
          style={{
            ...styles.btnAptitud,
            background:
              filtroAptitudGeneral === "aptos" ? "#16a34a" : "#dcfce7",
            color: filtroAptitudGeneral === "aptos" ? "#fff" : "#166534",
          }}
        >
          ✔ APTOS ({resumenAptitud.cantidadAptos})
        </button>

        <button
          type="button"
          onClick={() => setFiltroAptitudGeneral("no_aptos")}
          style={{
            ...styles.btnAptitud,
            background:
              filtroAptitudGeneral === "no_aptos" ? "#dc2626" : "#fee2e2",
            color: filtroAptitudGeneral === "no_aptos" ? "#fff" : "#991b1b",
          }}
        >
          ✖ NO APTOS ({resumenAptitud.cantidadNoAptos})
        </button>

        <button
          type="button"
          onClick={() => setFiltroAptitudGeneral("pendientes")}
          style={{
            ...styles.btnAptitud,
            background:
              filtroAptitudGeneral === "pendientes" ? "#d97706" : "#fef3c7",
            color: filtroAptitudGeneral === "pendientes" ? "#fff" : "#92400e",
          }}
        >
          ⏳ PENDIENTES ({resumenAptitud.cantidadPendientes})
        </button>

        <button
          type="button"
          onClick={() => setFiltroAptitudGeneral("todos")}
          style={{
            ...styles.btnAptitud,
            background:
              filtroAptitudGeneral === "todos" ? "#2563eb" : "#e5e7eb",
            color: filtroAptitudGeneral === "todos" ? "#fff" : "#111827",
          }}
        >
          👥 TODOS ({dataLocal.length})
        </button>

        <button
          type="button"
          onClick={descargarExcelAptitud}
          style={styles.btnExcelAptitud}
          disabled={!dataLocal.length}
        >
          📊 Excel APTO / NO APTO
        </button>
      </div>

      <div style={styles.barraEvaluaciones}>
        <select
          value={filtroEvaluaciones}
          onChange={(e) => {
            setFiltroEvaluaciones(e.target.value);
            setEvaluacionesSeleccionadas({});
          }}
          style={styles.selectFiltroEvaluacion}
        >
          <option value="pendientes">
            Mostrar pendientes de evaluar
          </option>
          <option value="evaluados">
            Mostrar ya evaluados
          </option>
          <option value="todos">
            Mostrar todos
          </option>
        </select>

        <button
          type="button"
          onClick={toggleSeleccionarTodasEvaluaciones}
          disabled={!evaluacionesVisibles.length}
          style={styles.btnSeleccionarEvaluaciones}
        >
          {todasLasEvaluacionesVisiblesSeleccionadas
            ? "Quitar selección visible"
            : "Seleccionar evaluaciones visibles"}
        </button>

        <button
          type="button"
          onClick={calificarSeleccionadasMasivamente}
          disabled={
            !cantidadSeleccionadas ||
            guardandoCalificacionMasiva
          }
          style={{
            ...styles.btnCalificarMasivo,
            opacity:
              cantidadSeleccionadas &&
              !guardandoCalificacionMasiva
                ? 1
                : 0.55,
          }}
        >
          {guardandoCalificacionMasiva
            ? "Guardando..."
            : `APTO / NO APTO (${cantidadSeleccionadas})`}
        </button>

        {cantidadSeleccionadas > 0 && (
          <button
            type="button"
            onClick={limpiarSeleccionEvaluaciones}
            style={styles.btnLimpiarSeleccion}
          >
            Limpiar selección
          </button>
        )}

        <span style={styles.contadorEvaluaciones}>
          Evaluaciones visibles: {evaluacionesVisibles.length}
        </span>
      </div>

      <div style={styles.resumenContainer}>
        {!esPerfeccionamiento && (
          <div style={styles.cardResumen}>
            <b>👥 Géneros</b>

            {Object.entries(resumenGeneros).map(([genero, r]) => (
              <div key={genero} style={styles.resumenTexto}>
                <b>{genero}:</b> Matriculados {r.matriculados} | Presentes{" "}
                {r.presentes}
              </div>
            ))}
          </div>
        )}

        <div style={styles.cardMatriculados}>
          <b>Inscriptos en etapa</b>

          <div style={styles.numeroMatriculados}>
            {cantidadMatriculadosEtapa}
          </div>

          <div style={styles.resumenTexto}>Filtrados: {filteredData.length}</div>
        </div>

        {!esPerfeccionamiento && (
          <div style={styles.cardPresentes}>
            <b>
              ✔ Presentes
              {filtroSede !== "todos"
                ? ` - ${getNombreSede(filtroSede)}`
                : ""}
            </b>

            <div style={styles.numeroMatriculados}>
              {resumenPresentes.totalPersonas}
            </div>

            <div style={styles.resumenTexto}>
              Personas con presente
            </div>

            <div style={styles.resumenTexto}>
              Registros totales: {resumenPresentes.totalRegistros}
            </div>

            {Object.entries(
              resumenPresentes.porDependencia
            ).map(([dependencia, cantidad]) => (
              <div
                key={dependencia}
                style={styles.resumenTexto}
              >
                <b>{dependencia}:</b> {cantidad}
              </div>
            ))}

            {Object.entries(resumenPresentes.porFecha)
              .sort(([fechaA], [fechaB]) => fechaB.localeCompare(fechaA))
              .map(([fecha, cant]) => {
                const restantes = Math.max(
                  cantidadMatriculadosEtapa - cant,
                  0
                );

                const fechaActiva = filtroPresenteFecha === fecha;

                return (
                  <div
                    key={fecha}
                    style={{
                      ...styles.resumenFechaAsistencia,
                      borderColor: fechaActiva ? "#2563eb" : "#bbf7d0",
                    }}
                  >
                    <div style={styles.resumenFechaTitulo}>
                      📅 {fecha}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setFiltroPresenteFecha(fecha);
                        setFiltroTipoAsistencia("presentes");
                      }}
                      style={{
                        ...styles.btnResumenPresente,
                        outline:
                          fechaActiva &&
                          filtroTipoAsistencia === "presentes"
                            ? "2px solid #15803d"
                            : "none",
                      }}
                    >
                      ✔ Presentes: <b>{cant}</b>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setFiltroPresenteFecha(fecha);
                        setFiltroTipoAsistencia("restantes");
                      }}
                      style={{
                        ...styles.btnResumenRestan,
                        outline:
                          fechaActiva &&
                          filtroTipoAsistencia === "restantes"
                            ? "2px solid #d97706"
                            : "none",
                      }}
                    >
                      ⏳ Restan: <b>{restantes}</b>
                    </button>

                    <div style={styles.resumenFechaTotal}>
                      👥 Total: <b>{cantidadMatriculadosEtapa}</b>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {Object.values(resumenPorInstancia).map((r, i) => (
          <div key={i} style={styles.cardResumen}>
            <b>{r.nombre}</b>

            <div style={styles.resumenTexto}>✔ Completados {r.aprobado}</div>
            <div style={styles.resumenTexto}>● En proceso {r.en_progreso}</div>
            <div style={styles.resumenTexto}>✖ Pendientes {r.pendiente}</div>

            <hr />

            <div style={{ marginTop: 8 }}>
              <b>📌 Items</b>

              {Object.entries(r.items).map(([nombre, datos]) => {
                const itemReal = dataLocal
                  .flatMap((p) => p.instancias || [])
                  .flatMap((inst) => inst.items || [])
                  .find((it) => getNombreItem(it) === nombre);

                return (
                  <div key={nombre} style={styles.itemResumenCard}>
                    <div style={{ fontWeight: "bold" }}>{nombre}</div>

                    <div>✔ {datos.aprobado}</div>
                    <div>● {datos.en_progreso}</div>
                    <div>✖ {datos.pendiente}</div>

                    {itemReal && (
                      <button
                        onClick={() => descargarZipItem(itemReal)}
                        style={styles.btnDescargarZip}
                      >
                        📦 Descargar ZIP
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div style={styles.buscadorContainer}>
        <input
          placeholder="Buscar persona"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.inputBuscar}
        />

        <select
          value={ordenAlfabetico}
          onChange={(e) => setOrdenAlfabetico(e.target.value)}
          style={styles.selectOrdenAlfabetico}
          title="Ordenar filas"
        >
          <option value="apellido_asc">
            Apellido A → Z
          </option>
          <option value="apellido_desc">
            Apellido Z → A
          </option>
          <option value="nombre_asc">
            Nombre A → Z
          </option>
          <option value="nombre_desc">
            Nombre Z → A
          </option>
        </select>

        <button onClick={exportToExcel}>Excel</button>

        {!esPerfeccionamiento && (
          <button onClick={exportPresentesExcel}>Excel Presentes</button>
        )}
      </div>

      <div style={styles.paginador}>
        <button
          onClick={() => setPaginaActual((p) => Math.max(p - 1, 1))}
          disabled={paginaActual === 1}
        >
          ⬅ Anterior
        </button>

        <span>
          Página {paginaActual} de {totalPaginas}
          {" · "}
          {dataOrdenada.length > 0
            ? `${(paginaActual - 1) * filasPorPagina + 1}-${Math.min(
                paginaActual * filasPorPagina,
                dataOrdenada.length
              )} de ${dataOrdenada.length}`
            : "0 registros"}
          {" · "}posición guardada
        </span>

        <button
          onClick={() =>
            setPaginaActual((p) => Math.min(p + 1, totalPaginas))
          }
          disabled={paginaActual === totalPaginas}
        >
          Siguiente ➡
        </button>
      </div>

      <div className="ise-correccion-toolbar">
        <label><input type="checkbox" checked={vistaCompacta} onChange={(e) => setVistaCompacta(e.target.checked)} /> Tabla compacta</label>
        <button type="button" onClick={() => {
          if (!itemCorreccionId && itemsNumericosDisponibles.length) setItemCorreccionId(itemsNumericosDisponibles[0].id);
          setModoCorreccion((v) => !v);
        }} disabled={!itemsNumericosDisponibles.length}>
          {modoCorreccion ? "Volver al listado" : "✍ Corrección rápida"}
        </button>
        {modoCorreccion && <>
          <select aria-label="Evaluación a corregir" value={itemCorreccionId} onChange={(e) => setItemCorreccionId(e.target.value)}>
            {itemsNumericosDisponibles.map((it) => <option key={it.id} value={it.id}>{it.instancia} · {it.nombre}</option>)}
          </select>
          <label><input type="checkbox" checked={soloSinNota} onChange={(e) => setSoloSinNota(e.target.checked)} /> Solo sin nota</label>
          <strong>{filasCorreccion.length} postulantes</strong>
        </>}
      </div>

      {modoCorreccion && (
        <div className="ise-correccion-panel">
          <div className="ise-correccion-ayuda">Enter guarda y avanza al siguiente postulante. Guardar mantiene el foco actual. Cada nota se registra con auditoría. El listado respeta los filtros activos.</div>
          <div className="ise-correccion-head"><span>Postulante</span><span>Nota / Guardar</span></div>
          {filasCorreccion.map((fila, idx) => {
            const instancia = fila.instancias?.find((inst) => inst.items?.some((it) => String(getItemIdReal(it)) === String(itemCorreccionId)));
            const item = instancia?.items?.find((it) => String(getItemIdReal(it)) === String(itemCorreccionId));
            if (!item) return null;
            return <div className="ise-correccion-fila" data-dni={String(fila.persona?.dni ?? "")} key={`${fila.persona?.dni}-${itemCorreccionId}`}>
              <div className="ise-correccion-nombre"><b>{idx + 1}. {fila.persona?.apellido}, {fila.persona?.nombre}</b><small>DNI {fila.persona?.dni}</small></div>
              <CampoNota item={item} persona={fila.persona} instancia={instancia} usuario={Usuario}
                procesoId={etapa.proceso_id || procesoSeleccionado?.id}
                onGuardado={async () => { await fetchData({ silencioso: true }); }}
                onAvanzar={() => {
                  const siguiente = filasCorreccion[idx + 1];
                  if (!siguiente) return;
                  const siguienteDni = String(siguiente.persona?.dni ?? "");
                  const inputSiguiente = Array.from(
                    document.querySelectorAll('.ise-correccion-fila[data-dni] input[aria-label]')
                  ).find((input) => input.closest('.ise-correccion-fila')?.dataset.dni === siguienteDni);
                  if (inputSiguiente) {
                    inputSiguiente.focus();
                    inputSiguiente.select();
                    inputSiguiente.scrollIntoView({ block: "nearest", behavior: "smooth" });
                  }
                }} />
            </div>;
          })}
          {!filasCorreccion.length && <p>No hay postulantes para esta evaluación con los filtros seleccionados.</p>}
        </div>
      )}

      {!modoCorreccion && !isMobile && (
        <div className={vistaCompacta ? "ise-tabla-compacta" : "ise-tabla-normal"}>
        <table style={styles.tabla}>
          <thead>
            <tr>
              <th style={styles.th}>#</th>
              <th style={styles.th}>DNI</th>
              <th style={styles.th}>Apellido</th>
              <th style={styles.th}>Nombre</th>
              <th style={styles.th}>Género</th>
              <th style={styles.th}>Acciones</th>

              {!esPerfeccionamiento && (
                <>
                  <th style={styles.th}>Presentes</th>
                  <th style={styles.th}>Sede</th>
                </>
              )}

              {data[0]?.instancias?.map((inst) => (
                <th key={inst.id} style={styles.th}>
                  {inst.nombre}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {dataPaginada.map((p, index) => (
              <tr
                key={p.persona.dni}
                style={{
                  background: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                }}
              >
                <td style={styles.td}>
                  {(paginaActual - 1) * filasPorPagina + index + 1}
                </td>

                <td style={styles.td}>{p.persona.dni}</td>
                <td style={styles.td}>{p.persona.apellido}</td>
                <td style={styles.td}>{p.persona.nombre}</td>
                <td style={styles.td}>
                  {p.persona.genero || p.persona.sexo || "—"}
                </td>

                <td style={styles.td}>
                  <button
                    type="button"
                    onClick={() => desmatricularPersona(p.persona)}
                    style={styles.btnDesmatricularIndividual}
                    disabled={desmatriculando}
                    title={`Desmatricular a ${p.persona.apellido}, ${p.persona.nombre}`}
                  >
                    🗑 Desmatricular
                  </button>
                </td>

                {!esPerfeccionamiento && (
                  <td style={styles.td}>
                    {getPresentesPersona(p).length > 0 ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 5,
                          minWidth: 185,
                        }}
                      >
                        {(vistaCompacta ? getPresentesPersona(p).slice(0, 1) : getPresentesPersona(p)).map(
                          (presente, indexPresente) => (
                            <button
                              key={
                                presente?.id ||
                                `${presente?.dependencia}-${presente?.fecha}-${presente?.hora}-${indexPresente}`
                              }
                              type="button"
                              onClick={() =>
                                mostrarPresentes(
                                  getPresentesPersona(p)
                                )
                              }
                              style={{
                                border: "none",
                                borderRadius: 7,
                                background:
                                  getColorDependencia(
                                    presente?.dependencia
                                  ),
                                color:
                                  getColorTextoDependencia(
                                    presente?.dependencia
                                  ),
                                padding: "5px 7px",
                                cursor: "pointer",
                                textAlign: "left",
                                fontSize: 11,
                                lineHeight: 1.25,
                              }}
                              title="Ver detalle de todos los presentes"
                            >
                              <div
                                style={{
                                  fontWeight: 800,
                                  marginBottom: 2,
                                }}
                              >
                                {presente?.dependencia ||
                                  "Sin dependencia"}
                              </div>

                              <div>
                                ✔ {presente?.hora || "Sin hora"}
                              </div>

                              <div style={{ fontSize: 9, opacity: 0.95 }}>
                                📍 {getNombreSede(presente?.sedeID)}
                              </div>

                              <div style={{ fontSize: 9, opacity: 0.9 }}>
                                {presente?.usuario || ""}
                              </div>
                            </button>
                          )
                        )}
                        {vistaCompacta && getPresentesPersona(p).length > 1 && (
                          <button type="button" onClick={() => mostrarPresentes(getPresentesPersona(p))} title="Ver todos los presentes">
                            +{getPresentesPersona(p).length - 1} presentes
                          </button>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: "#999" }}>
                        —
                      </span>
                    )}
                  </td>
                )}

                {!esPerfeccionamiento && (
                  <td style={styles.td}>
                    {getSedesPersona(p).length > 0 ? (
                      <div style={styles.sedesContainer}>
                        {getSedesPersona(p).map((sede) => (
                          <span
                            key={sede}
                            style={styles.sedeChip}
                          >
                            📍 {sede}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: "#999" }}>—</span>
                    )}
                  </td>
                )}

                {p.instancias?.map((inst) => (
                  <td key={inst.id} style={styles.td}>
                    <div style={styles.itemsContainer}>
                      {inst.items
                        ?.filter((item) =>
                          String(item.visible || "")
                            .toLowerCase()
                            .includes("reclutamiento")
                        )
                        .map((item, i) => {
                          const clave = getClaveEvaluacion(
                            p,
                            inst,
                            item
                          );

                          const seleccionado =
                            Boolean(
                              evaluacionesSeleccionadas[clave]
                            );

                          return (
                            <div
                              key={
                                item.persona_proceso_item_id ||
                                getItemIdReal(item)
                              }
                              style={styles.itemConCheck}
                            >
                              {esEvaluacion(item) && !configNota(item) && (
                                <input
                                  type="checkbox"
                                  checked={seleccionado}
                                  onChange={() =>
                                    toggleEvaluacionSeleccionada(
                                      p,
                                      inst,
                                      item
                                    )
                                  }
                                  onClick={(e) =>
                                    e.stopPropagation()
                                  }
                                  title="Seleccionar evaluación"
                                />
                              )}

                              <span
                                title={
                                  item.tipo === "archivo"
                                    ? "Archivo: tocar para marcar o ver archivo"
                                    : getNombreItem(item)
                                }
                                onClick={() => {
                                  if (!configNota(item)) handleClickItem(item, p.persona, inst);
                                }}
                                style={{
                                  ...styles.itemChip,
                                  background: getColor(item),
                                  outline: seleccionado
                                    ? "2px solid #2563eb"
                                    : "none",
                                  border: itemCumpleFechaArchivo(
                                    item,
                                    filtroFechaArchivoDesde
                                  )
                                    ? "2px solid #dc2626"
                                    : item.tipo === "archivo"
                                    ? "1px solid #2563eb"
                                    : "none",
                                  boxShadow: itemCumpleFechaArchivo(
                                    item,
                                    filtroFechaArchivoDesde
                                  )
                                    ? "0 0 0 2px rgba(220,38,38,0.15)"
                                    : "none",
                                }}
                              >
                                <div style={styles.itemChipContenido}>
                                  <div>
                                    {getTextoTipoArchivo(item)}
                                    {getNombreItem(item) ||
                                      `Item ${i + 1}`}{" "}
                                    {getIcon(item)}
                                  </div>

                                  {getFechaArchivo(item) && (
                                    <div style={styles.fechaArchivo}>
                                      🕒 {getFechaArchivo(item)}
                                    </div>
                                  )}

                                  {itemCumpleFechaArchivo(
                                    item,
                                    filtroFechaArchivoDesde
                                  ) && (
                                    <div style={styles.badgeFiltroFecha}>
                                      Subido desde la fecha elegida
                                    </div>
                                  )}
                                </div>
                              </span>
                              <CampoNota item={item} persona={p.persona} instancia={inst} usuario={Usuario} procesoId={etapa.proceso_id || procesoSeleccionado?.id} onGuardado={()=>fetchData({silencioso:true})}/>
                            </div>
                          );
                        })}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {!modoCorreccion && isMobile && (
        <div style={{ marginTop: 10 }}>
          {dataPaginada.map((p, index) => (
            <div
              key={p.persona.dni}
              style={{
                ...styles.cardMobile,
                background: index % 2 === 0 ? "#fff" : "#f8fafc",
              }}
            >
              <b>
                #{(paginaActual - 1) * filasPorPagina + index + 1} -{" "}
                {p.persona.apellido}, {p.persona.nombre}
              </b>

              <div>DNI: {p.persona.dni}</div>
              <div>Género: {p.persona.genero || p.persona.sexo || "—"}</div>

              <button
                type="button"
                onClick={() => desmatricularPersona(p.persona)}
                style={styles.btnDesmatricularIndividualMobile}
                disabled={desmatriculando}
              >
                🗑 Desmatricular de esta etapa
              </button>

              {!esPerfeccionamiento &&
                getPresentesPersona(p).length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 5,
                      marginTop: 6,
                    }}
                  >
                    {getPresentesPersona(p).map(
                      (presente, indexPresente) => (
                        <div
                          key={
                            presente?.id ||
                            `${presente?.dependencia}-${presente?.fecha}-${presente?.hora}-${indexPresente}`
                          }
                          onClick={() =>
                            mostrarPresentes(
                              getPresentesPersona(p)
                            )
                          }
                          style={{
                            ...styles.presenteMobile,
                            background:
                              getColorDependencia(
                                presente?.dependencia
                              ),
                            color:
                              getColorTextoDependencia(
                                presente?.dependencia
                              ),
                          }}
                        >
                          <b>
                            ✔{" "}
                            {presente?.dependencia ||
                              "Sin dependencia"}
                          </b>

                          <div>
                            {presente?.fecha || "—"}{" "}
                            {presente?.hora || "—"}
                          </div>

                          <div style={{ fontSize: 10 }}>
                            📍 {getNombreSede(presente?.sedeID)}
                          </div>

                          {presente?.usuario && (
                            <div style={{ fontSize: 10, opacity: 0.9 }}>
                              {presente.usuario}
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                )}

              {p.instancias?.map((inst) => (
                <div key={inst.id} style={{ marginTop: 8 }}>
                  <b>{inst.nombre}</b>

                  <div style={styles.itemsContainer}>
                    {inst.items
                      .filter((item) =>
                        String(item.visible || "")
                          .toLowerCase()
                          .includes("reclutamiento")
                      )
                      .map((item, i) => {
                        const clave = getClaveEvaluacion(
                          p,
                          inst,
                          item
                        );

                        const seleccionado =
                          Boolean(
                            evaluacionesSeleccionadas[clave]
                          );

                        return (
                          <div
                            key={
                              item.persona_proceso_item_id ||
                              getItemIdReal(item)
                            }
                            style={styles.itemConCheckMobile}
                          >
                            {esEvaluacion(item) && !configNota(item) && (
                              <input
                                type="checkbox"
                                checked={seleccionado}
                                onChange={() =>
                                  toggleEvaluacionSeleccionada(
                                    p,
                                    inst,
                                    item
                                  )
                                }
                                onClick={(e) =>
                                  e.stopPropagation()
                                }
                                title="Seleccionar evaluación"
                              />
                            )}

                            <span
                              title={
                                item.tipo === "archivo"
                                  ? "Archivo: tocar para marcar o ver archivo"
                                  : getNombreItem(item)
                              }
                              onClick={() => {
                                if (!configNota(item)) handleClickItem(item, p.persona, inst);
                              }}
                              style={{
                                ...styles.itemChipMobile,
                                background: getColor(item),
                                outline: seleccionado
                                  ? "2px solid #2563eb"
                                  : "none",
                                border: itemCumpleFechaArchivo(
                                  item,
                                  filtroFechaArchivoDesde
                                )
                                  ? "2px solid #dc2626"
                                  : item.tipo === "archivo"
                                  ? "1px solid #2563eb"
                                  : "none",
                                boxShadow: itemCumpleFechaArchivo(
                                  item,
                                  filtroFechaArchivoDesde
                                )
                                  ? "0 0 0 2px rgba(220,38,38,0.15)"
                                  : "none",
                              }}
                            >
                              {getTextoTipoArchivo(item)}
                              {getNombreItem(item) ||
                                `Item ${i + 1}`}{" "}
                              {getIcon(item)}
                            </span>
                            <CampoNota item={item} persona={p.persona} instancia={inst} usuario={Usuario} procesoId={etapa.proceso_id || procesoSeleccionado?.id} onGuardado={()=>fetchData({silencioso:true})}/>
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {filteredData.length === 0 && <p>No hay inscriptos</p>}

      {modalDesmatricularOpen && (
        <Modal
          onClose={() => {
            if (desmatriculando) return;
            setModalDesmatricularOpen(false);
            setDnisDesmatricularTexto("");
          }}
        >
          <div style={styles.modalDesmatricular}>
            <h3 style={styles.tituloDesmatricular}>
              🗑 Desmatricular personas de la etapa
            </h3>

            <p style={styles.textoDesmatricular}>
              Pegá uno o varios DNI separados por espacios, comas,
              punto y coma o saltos de línea.
            </p>

            <textarea
              value={dnisDesmatricularTexto}
              onChange={(e) => setDnisDesmatricularTexto(e.target.value)}
              placeholder={`Ejemplo:
40123456
40987654, 41222333
42555666`}
              style={styles.textareaDesmatricular}
              disabled={desmatriculando}
            />

            <div style={styles.contadorDnis}>
              DNI detectados:{" "}
              <b>{normalizarDnis(dnisDesmatricularTexto).length}</b>
            </div>

            <div style={styles.accionesModalDesmatricular}>
              <button
                type="button"
                onClick={() => {
                  setModalDesmatricularOpen(false);
                  setDnisDesmatricularTexto("");
                }}
                style={styles.btnCancelarDesmatricular}
                disabled={desmatriculando}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={desmatricularMasivamente}
                style={styles.btnConfirmarDesmatricular}
                disabled={
                  desmatriculando ||
                  normalizarDnis(dnisDesmatricularTexto).length === 0
                }
              >
                {desmatriculando
                  ? "Procesando..."
                  : "🗑 Revisar y desmatricular"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {modalOpen && itemSeleccionado && (
        <Modal
          onClose={() => {
            setModalOpen(false);
            setItemSeleccionado(null);
            setTipoModal(null);

            fetchData({ silencioso: true });
          }}
        >
          {tipoModal === "archivo" && (
            <UploadArchivosItem
              datos={itemSeleccionado}
              etapa={etapa}
              refres={fetchData}
              soloLectura={true}
            />
          )}

          {tipoModal === "carpeta_medica" && (
            <CarpetaMedicaItem
              datos={itemSeleccionado}
              etapa={etapa}
              refres={fetchData}
            />
          )}
          {tipoModal === "completoPerfeccionamiento" && (
            <ModuloCompletoPerfeccionamiento
              datos={itemSeleccionado}
              etapa={etapa}
              refres={fetchData}
            />
          )}
          {tipoModal === "evaluacion_medica" && (
            <EvaluacionMedicaItem
              datos={itemSeleccionado}
              etapa={etapa}
              refres={fetchData}
            />
          )}

          {tipoModal === "psicologia" && (
            <ModuloPiscologiaIngreso datos={itemSeleccionado} etapa={etapa} />
          )}
        </Modal>
      )}
    </div>
  );
};

const Modal = ({ children, onClose }) => {
  return (
    <div style={styles.modalFondo}>
      <div style={styles.modalContenido}>
        <button onClick={onClose} style={styles.btnCerrar}>
          ❌
        </button>

        {children}
      </div>
    </div>
  );
};

const styles = {
  barraAptitudGeneral: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
    padding: 12,
    margin: "12px 0",
    border: "1px solid #dbeafe",
    borderRadius: 10,
    background: "#f8fafc",
  },
  tituloAptitudGeneral: {
    width: "100%",
    fontWeight: "bold",
    marginBottom: 4,
  },
  btnAptitud: {
    border: "none",
    borderRadius: 8,
    padding: "9px 13px",
    fontWeight: "bold",
    cursor: "pointer",
  },
  btnExcelAptitud: {
    border: "none",
    borderRadius: 8,
    padding: "9px 13px",
    fontWeight: "bold",
    cursor: "pointer",
    background: "#166534",
    color: "#fff",
    marginLeft: "auto",
  },
  page: {
    background: "#fff",
    padding: 15,
    marginTop: 10,
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    userSelect: "text",
    WebkitUserSelect: "text",
    MozUserSelect: "text",
    msUserSelect: "text",
  },
  accionesDesmatriculacion: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 10,
    marginBottom: 10,
  },
  btnDesmatricularMasivo: {
    background: "#b91c1c",
    color: "#fff",
    border: "none",
    padding: "9px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: "bold",
  },
  btnDesmatricularIndividual: {
    background: "#dc2626",
    color: "#fff",
    border: "none",
    padding: "5px 8px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 11,
    whiteSpace: "nowrap",
  },
  btnDesmatricularIndividualMobile: {
    marginTop: 8,
    width: "100%",
    background: "#dc2626",
    color: "#fff",
    border: "none",
    padding: "7px 10px",
    borderRadius: 7,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: "bold",
  },
  modalDesmatricular: {
    width: "min(650px, 88vw)",
    paddingTop: 8,
  },
  tituloDesmatricular: {
    marginTop: 0,
    marginBottom: 8,
    color: "#991b1b",
  },
  textoDesmatricular: {
    marginTop: 0,
    color: "#475569",
    fontSize: 14,
  },
  textareaDesmatricular: {
    width: "100%",
    minHeight: 220,
    resize: "vertical",
    padding: 12,
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    fontFamily: "inherit",
    fontSize: 14,
    boxSizing: "border-box",
  },
  contadorDnis: {
    marginTop: 8,
    padding: "7px 10px",
    background: "#f1f5f9",
    borderRadius: 7,
    fontSize: 13,
  },
  accionesModalDesmatricular: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 14,
  },
  btnCancelarDesmatricular: {
    background: "#64748b",
    color: "#fff",
    border: "none",
    padding: "8px 12px",
    borderRadius: 7,
    cursor: "pointer",
  },
  btnConfirmarDesmatricular: {
    background: "#dc2626",
    color: "#fff",
    border: "none",
    padding: "8px 12px",
    borderRadius: 7,
    cursor: "pointer",
    fontWeight: "bold",
  },
  btnDescargarZip: {
    marginTop: 6,
    background: "#2563eb",
    color: "white",
    border: "none",
    padding: "5px 8px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 12,
  },
  filtrosActivos: {
    marginTop: 10,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  btnFiltroActivo: {
    background: "#ef4444",
    color: "white",
    border: "none",
    padding: "6px 10px",
    borderRadius: 6,
    cursor: "pointer",
  },
  selectSedeAsistencia: {
    padding: "7px 10px",
    borderRadius: 7,
    border: "1px solid #86efac",
    background: "#fff",
    minWidth: 165,
    cursor: "pointer",
  },

  filtroSedeActivo: {
    marginTop: 10,
    padding: "8px 10px",
    borderRadius: 8,
    background: "#e0f2fe",
    border: "1px solid #7dd3fc",
    color: "#075985",
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    fontSize: 13,
  },

  btnLimpiarSede: {
    padding: "4px 8px",
    borderRadius: 6,
    border: "1px solid #0284c7",
    background: "#fff",
    color: "#075985",
    fontWeight: "bold",
    cursor: "pointer",
  },

  sedesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  sedeChip: {
    display: "inline-block",
    padding: "5px 8px",
    borderRadius: 7,
    background: "#e0f2fe",
    border: "1px solid #7dd3fc",
    color: "#075985",
    fontWeight: "bold",
    fontSize: 11,
    width: "fit-content",
  },

  controlAsistenciaFecha: {
    marginTop: 10,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    padding: "10px 12px",
    border: "1px solid #bfdbfe",
    borderRadius: 8,
    background: "#eff6ff",
  },
  controlAsistenciaTitulo: {
    fontWeight: "bold",
    color: "#1e3a8a",
    marginRight: 4,
  },
  inputFechaAsistencia: {
    padding: "7px 9px",
    borderRadius: 7,
    border: "1px solid #93c5fd",
    background: "#fff",
  },
  btnAsistenciaFecha: {
    padding: "7px 10px",
    borderRadius: 7,
    border: "none",
    cursor: "pointer",
    fontWeight: "bold",
  },
  btnLimpiarFechaAsistencia: {
    padding: "7px 10px",
    borderRadius: 7,
    border: "none",
    cursor: "pointer",
    background: "#64748b",
    color: "#fff",
  },
  resumenFechaAsistencia: {
    marginTop: 10,
    padding: 8,
    border: "1px solid #bbf7d0",
    borderRadius: 8,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },
  resumenFechaTitulo: {
    fontWeight: "bold",
    color: "#0f172a",
    marginBottom: 2,
  },
  btnResumenPresente: {
    border: "none",
    borderRadius: 6,
    padding: "6px 8px",
    background: "#dcfce7",
    color: "#166534",
    cursor: "pointer",
    textAlign: "left",
  },
  btnResumenRestan: {
    border: "none",
    borderRadius: 6,
    padding: "6px 8px",
    background: "#fef3c7",
    color: "#92400e",
    cursor: "pointer",
    textAlign: "left",
  },
  resumenFechaTotal: {
    fontSize: 12,
    color: "#475569",
    padding: "2px 4px",
  },
  turnosContainer: {
    marginTop: 10,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  btnTurno: {
    padding: "6px 10px",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
  },
  filtroItemContainer: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 10,
    alignItems: "center",
  },
  selectFiltro: {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #ccc",
    minWidth: 220,
  },
  filtroFechaArchivo: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    padding: "7px 10px",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#f8fafc",
  },
  labelFiltroFecha: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#334155",
  },
  inputFechaArchivo: {
    padding: "7px 9px",
    borderRadius: 7,
    border: "1px solid #94a3b8",
    background: "#fff",
  },
  btnLimpiarFecha: {
    padding: "7px 9px",
    borderRadius: 7,
    border: "none",
    background: "#64748b",
    color: "#fff",
    cursor: "pointer",
  },
  barraEvaluaciones: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 12,
    padding: 10,
    border: "1px solid #bfdbfe",
    borderRadius: 8,
    background: "#eff6ff",
  },
  selectFiltroEvaluacion: {
    padding: "8px 10px",
    borderRadius: 7,
    border: "1px solid #94a3b8",
    minWidth: 220,
    background: "#fff",
  },
  btnSeleccionarEvaluaciones: {
    padding: "8px 11px",
    borderRadius: 7,
    border: "1px solid #94a3b8",
    background: "#fff",
    cursor: "pointer",
  },
  btnCalificarMasivo: {
    padding: "8px 12px",
    borderRadius: 7,
    border: "1px solid #1d4ed8",
    background: "#2563eb",
    color: "#fff",
    fontWeight: "bold",
    cursor: "pointer",
  },
  btnLimpiarSeleccion: {
    padding: "8px 10px",
    borderRadius: 7,
    border: "1px solid #cbd5e1",
    background: "#f8fafc",
    cursor: "pointer",
  },
  contadorEvaluaciones: {
    marginLeft: "auto",
    fontSize: 12,
    color: "#475569",
  },
  itemConCheck: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  itemConCheckMobile: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    width: "100%",
  },
  resumenContainer: {
    display: "flex",
    gap: 15,
    flexWrap: "wrap",
    marginTop: 10,
  },
  cardMatriculados: {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 10,
    minWidth: 180,
    background: "#eff6ff",
  },
  numeroMatriculados: {
    fontSize: 22,
    marginTop: 5,
    fontWeight: "bold",
  },
  cardPresentes: {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 10,
    minWidth: 200,
    background: "#ecfdf5",
  },
  fechaPresente: {
    cursor: "pointer",
    fontSize: 13,
    marginTop: 4,
  },
  cardResumen: {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 10,
    minWidth: 180,
    background: "#fafafa",
  },
  resumenTexto: {
    fontSize: 13,
    marginTop: 5,
  },
  itemResumenCard: {
    fontSize: 12,
    marginTop: 6,
    padding: 6,
    background: "#f8fafc",
    borderRadius: 6,
  },
  buscadorContainer: {
    display: "flex",
    gap: 10,
    marginTop: 10,
  },
  inputBuscar: {
    flex: 1,
    padding: 8,
    borderRadius: 8,
    border: "1px solid #ccc",
  },
  selectOrdenAlfabetico: {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #94a3b8",
    background: "#fff",
    minWidth: 170,
    cursor: "pointer",
  },
  paginador: {
    marginTop: 15,
    display: "flex",
    gap: 10,
    alignItems: "center",
  },
  tabla: {
    width: "100%",
    minWidth: 1400,
    marginTop: 10,
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    border: "1px solid #ddd",
    padding: "8px 6px",
    background: "#e5e7eb",
    position: "sticky",
    top: 0,
    zIndex: 2,
    whiteSpace: "nowrap",
  },
  td: {
    border: "1px solid #e5e7eb",
    padding: "7px 6px",
    verticalAlign: "top",
    whiteSpace: "nowrap",
  },
  itemsContainer: {
    display: "flex",
    gap: 4,
    flexWrap: "wrap",
  },
  itemChip: {
    padding: "2px 6px",
    borderRadius: 4,
    display: "inline-block",
    fontSize: 12,
    cursor: "pointer",
  },
  itemChipMobile: {
    padding: "4px 6px",
    borderRadius: 6,
    display: "inline-block",
    fontSize: 12,
    cursor: "pointer",
  },
  itemChipContenido: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  fechaArchivo: {
    marginTop: 2,
    fontSize: 10,
    color: "#475569",
    fontWeight: "normal",
    whiteSpace: "nowrap",
  },
  badgeFiltroFecha: {
    marginTop: 2,
    padding: "2px 5px",
    borderRadius: 4,
    background: "#fee2e2",
    color: "#991b1b",
    fontSize: 9,
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },
  presenteChip: {
    cursor: "pointer",
    background: "#22c55e",
    color: "white",
    padding: "4px 8px",
    borderRadius: 6,
    fontSize: 12,
  },
  presenteMobile: {
    marginTop: 5,
    background: "#22c55e",
    color: "white",
    padding: 5,
    borderRadius: 6,
    cursor: "pointer",
  },
  cardMobile: {
    border: "1px solid #ddd",
    borderRadius: 8,
    marginBottom: 10,
    padding: 10,
  },
  modalFondo: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  modalContenido: {
    background: "#fff",
    padding: 20,
    borderRadius: 10,
    minWidth: 300,
    maxHeight: "90%",
    overflow: "auto",
  },
  btnCerrar: {
    float: "right",
  },
};

export default ListadoInscriptoEtapa;